import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table,
} from 'react-bootstrap';
import { Heading } from '@lomarengas/design-system';
import { useTranslation } from 'react-i18next';
import {
  ActionButtons,
  addStatusToast,
  InputText,
  Loader,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccommodationAccountRoleDto,
  BookingContainerUpdateItem,
  BookingDetailsData,
  BookingDetailsNoteItem,
  BookingDetailsOrderItem,
  CancelBooking,
  cancelBookingAsync,
  CancelContainer,
  cancelContainerAsync,
  CareTakerItem,
  ClientMessagePhase,
  CreateBookingPdf,
  detachFromBookingContainerAsync,
  DetachFromBookingContainerItem,
  getBookingContainerDetailsAsync,
  getBookingsMessageEventsAsync,
  getInvoiceDetailsPdfAsync,
  selectBookingDetails,
  selectBookingDetailsLoadingStatus,
  selectBookingMessageEvents,
  selectBookingMessageEventsStatus,
  sendBookingBillsMessageEmail,
  sendBookingCancellationMessageEmail,
  sendBookingConfirmationEmail,
  sendBookingConfirmationEmailToMaintainer,
  sendBookingConfirmationEmailToOwner,
  sendBookingPhaseMessageEmail,
  sendClientBillsMessageRequest,
  sendClientBookingConfirmationRequest,
  sendClientCancellationMessageRequest,
  sendClientPhaseMessageRequest,
  sendMaintainerBookingConfirmationRequest,
  sendOwnerBookingConfirmationRequest,
  setBasketContainer,
  setBookingContainer,
  setWebShopCustomerState,
  TravelAgencyData,
  updateBookingContainerAsync,
  updateBookingNoteAsync,
  UpdateBookingNoteItem,
} from './bookingsSlice';
import { LOADING_STATUS, LOCALES, ROLES } from '../../common/constants';
import {
  BookingType,
  bookingTypeTranslations,
} from './Constants/BookingTypeEnum';
import { NewBookingModal } from './Components/NewBookingModal';
import BasicBookingData from './Components/BasicBookingData';
import { AdditionalBookingModal } from './Components/AdditionalBookingModal';
import { CustomerNoteModal } from './Components/CustomerNoteModal';
import { OwnerNoteModal } from './Components/OwnerNoteModal';
import { OfficerNoteModal } from './Components/OfficerNoteModal';
import DeleteConfirmationWithReason from '../../common/components/DeleteConfirmation/DeleteConfirmationWithReason';
import {
  getCountriesAsync,
  selectCountryItems,
} from '../countries/countriesSlice';
import { checkRole } from '../auth/authSlice';
import { messageEventTypeTranslations } from './Constants/MessageEventTypeEnum';
import { isValidEmail, isValidEmails } from '../../common/utils/emailUtils';
import { BookingOwnersEmailComponent } from './Components/BookingOwnersEmailComponent';
import { BookingMaintainersEmailComponent } from './Components/BookingMaintainersEmailComponent';
import { useLocale } from '../../locales/useLocale';
import { formatUtcDateTimeToFinnish } from '../../common/utils/dateUtils';

function BookingDetails() {
  const { t } = useTranslation();
  const { currentLocale } = useLocale();
  const params = useParams();
  const state = useLocation();
  const dispatch = useAppDispatch();
  const bookingContainer = useAppSelector(selectBookingDetails);
  const countries = useAppSelector(selectCountryItems);
  const bookingDetailsLoadingStatus = useAppSelector(
    selectBookingDetailsLoadingStatus,
  );
  const BookingMessageEventsStatus = useAppSelector(
    selectBookingMessageEventsStatus,
  );
  const bookingMessageEventsContainer = useAppSelector(
    selectBookingMessageEvents,
  );
  const [
    clientBookingConfirmationToSentEmailAddress,
    setClientBookingConfirmationToSentEmailAddress,
  ] = useState<string>('');

  const [
    clientBookingPhase1ToSentEmailAddress,
    setClientBookingPhase1ToSentEmailAddress,
  ] = useState<string>('');

  const [
    clientBookingCancellationToSentEmailAddress,
    setClientBookingCancellationToSentEmailAddress,
  ] = useState<string>('');

  const [
    clientBookingBillsToSentEmailAddress,
    setClientBookingBillsToSentEmailAddress,
  ] = useState<string>('');
  const [
    clientBookingPhase2ToSentEmailAddress,
    setClientBookingPhase2ToSentEmailAddress,
  ] = useState<string>('');

  const [
    clientBookingPhase3ToSentEmailAddress,
    setClientBookingPhase3ToSentEmailAddress,
  ] = useState<string>('');

  const history = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showLoader, setshowLoader] = useState<boolean>(false);
  const [editCustomerNote, setEditCustomerNote] = useState<boolean>(false);
  const [customerNoteModalBookingId, setCustomerNoteModalBookingId] =
    useState<string>(null);
  const [customerNoteModalNoteId, setCustomerNoteModalNoteId] =
    useState<string>(null);
  const [editOwnerNote, setEditOwnerNote] = useState<boolean>(false);
  const [ownerNoteModalBookingId, setOwnerNoteModalBookingId] =
    useState<string>(null);
  const [ownerNoteModalNoteId, setOwnerNoteModalNoteId] =
    useState<string>(null);
  const [backLinkUrl, setbackLinkUrl] = useState<string>(
    '/bookings/search-bookings',
  );
  const [editOfficerNote, setEditOfficerNote] = useState<boolean>(false);
  const [officerNoteModalBookingId, setOfficerNoteModalBookingId] =
    useState<string>(null);
  const [officerNoteModalNoteId, setOfficerNoteModalNoteId] =
    useState<string>(null);
  const [bookingTypeParam, setBookingTypeParam] = useState<number>(null);
  const [showDeleteContainerModal, setShowDeleteContainerModal] =
    useState<boolean>(false);
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';

  const initFetch = useCallback(() => {
    if (params.bookingId !== 'search') {
      dispatch(getBookingContainerDetailsAsync(params.bookingId))
        .then(res => {
          // if (res.meta.requestStatus === 'rejected') {
          //   history(`/`);
          // }
          if (res.payload === null) {
            dispatch(setBookingContainer(null));
          }
          // Payer email state will be set when bookingContainer is available in a separate effect for fewer state updates
        })
        .catch(reason => {
          throw new Error(reason);
        });
      dispatch(getBookingsMessageEventsAsync(params.bookingId));
    }
    dispatch(
      getCountriesAsync({
        PageNumber: 1,
        PageSize: 100000,
        searchString: '',
        culture: currentLocale,
      }),
    );
  }, [dispatch, params.bookingId, currentLocale]);
  const [editedBooking, setEditedBooking] = useState<BookingDetailsData>(null);
  const [showEditBookingModal, setShowEditBookingModal] =
    useState<boolean>(false);
  const [showAdditionalBookingModal, setShowAdditionalBookingModal] =
    useState<boolean>(false);
  useEffect(() => {
    initFetch();
    return () => {
      // Clear booking-related Redux state and local component state on unmount
      dispatch(setBookingContainer(null));
      dispatch(setWebShopCustomerState(null));

      setEditedBooking(null);
      setShowEditBookingModal(false);
      setShowAdditionalBookingModal(false);

      setCustomerNoteModalBookingId(null);
      setOwnerNoteModalBookingId(null);
      setOfficerNoteModalBookingId(null);

      setClientBookingConfirmationToSentEmailAddress('');
      setClientBookingPhase1ToSentEmailAddress('');
      setClientBookingPhase2ToSentEmailAddress('');
      setClientBookingPhase3ToSentEmailAddress('');
      setClientBookingBillsToSentEmailAddress('');
      setClientBookingCancellationToSentEmailAddress('');
    };
  }, [initFetch]);
  useEffect(() => {
    if (bookingContainer != null && bookingContainer.bookingResponses != null) {
      const count = bookingContainer.bookingResponses.filter(
        item =>
          item.bookingType === 3 ||
          item.bookingType === 4 ||
          item.bookingType === 9 ||
          item.bookingType === 10,
      ).length;
      if (count > 0) {
        setbackLinkUrl('/?tab=tab1');
      }
      if (
        state !== null &&
        state.state !== null &&
        state.state.backUrl !== null &&
        state.state.backUrl !== undefined &&
        state.state.backUrl.length > 2
      ) {
        setbackLinkUrl(state.state.backUrl);
      }
      // set payer email to phase 1,2,3, cancellation boxes,
      // if it is found in bookingResponse
      // Payer email state is set in the bookingContainer effect below
    }
  }, [bookingContainer]);

  // Set payer email related state in one place to avoid repeated state updates across multiple effects
  useEffect(() => {
    if (
      bookingContainer?.bookingResponses &&
      bookingContainer.bookingResponses.length > 0
    ) {
      const firstResponse = bookingContainer.bookingResponses[0];
      const payerEmailAddress =
        firstResponse?.payerEmailAddress ||
        firstResponse?.webShopCustomer?.email ||
        '';
      if (payerEmailAddress) {
        setClientBookingConfirmationToSentEmailAddress(payerEmailAddress);
        setClientBookingPhase1ToSentEmailAddress(payerEmailAddress);
        setClientBookingPhase2ToSentEmailAddress(payerEmailAddress);
        setClientBookingPhase3ToSentEmailAddress(payerEmailAddress);
        setClientBookingBillsToSentEmailAddress(payerEmailAddress);
        setClientBookingCancellationToSentEmailAddress(payerEmailAddress);
      }
    }
  }, [bookingContainer]);

  function getAllowPastAdditional(booking) {
    let retval = null;
    if (booking && booking.bookingOrderItems) {
      if (booking.bookingOrderItems.some(i => i.orderItemType === 0)) {
        retval = booking.bookingOrderItems.find(i => i.orderItemType === 0);
      }
    }
    if (retval) {
      if (
        retval.startDateTime &&
        Date.parse(retval.startDateTime) < Date.now()
      ) {
        return true;
      }
      return false;
    }
    return false;
  }
  function getBookingTitle(booking) {
    let retval = '';
    if (booking && booking.bookingOrderItems) {
      retval = booking.bookingOrderItems
        .map(item => item.itemName)
        .filter(name => name) // Filter out any undefined or empty names
        .join(', ');

      // if (booking.bookingOrderItems.some(i => i.orderItemType === 0)) {
      //   retval = booking.bookingOrderItems.find(
      //     i => i.orderItemType === 0,
      //   )?.itemName;
      // } else {
      //   retval = booking.bookingOrderItems[0]?.itemName;
      // }
    }
    return retval;
  }
  const cancelBookingLocal = (bookingId: string, reason: string) => {
    setshowLoader(true);
    dispatch(
      cancelBookingAsync({
        bookingId,
        reasonOfCancellation: reason,
        expectedBookingType: editedBooking?.bookingType,
      } as CancelBooking),
    )
      .then(res => {
        if (res.meta.requestStatus === 'fulfilled') {
          dispatch(getBookingContainerDetailsAsync(params.bookingId));
        } else if (res.meta.requestStatus === 'rejected') {
          const error = res.payload as Record<string, unknown>;
          if (error?.message) {
            alert(error.message as string);
          }
        }
      })
      .finally(() => {
        setshowLoader(false);
      });
    setShowDeleteModal(false);
  };

  const detachBookingLocal = (bookingId: string, containerId: string) => {
    setshowLoader(true);
    dispatch(
      detachFromBookingContainerAsync({
        bookingId,
        bookingContainerId: containerId,
      } as DetachFromBookingContainerItem),
    )
      .then(res => {
        if (res.meta.requestStatus === 'fulfilled') {
          if (bookingId.toLowerCase() === params.bookingId.toLowerCase()) {
            const remainigBookingResponse =
              res.payload.result.bookingResponses &&
              res.payload.result.bookingResponses.length > 0
                ? res.payload.result.bookingResponses[0]
                : null;
            if (remainigBookingResponse) {
              dispatch(
                getBookingContainerDetailsAsync(remainigBookingResponse.id),
              );
            }
            window.open(`/bookings/${bookingId}`, '_blank');
          } else {
            window.open(`/bookings/${bookingId}`, '_blank');
            dispatch(getBookingContainerDetailsAsync(params.bookingId));
          }
        }
      })
      .finally(() => {
        setshowLoader(false);
      });
    setShowDeleteModal(false);
  };

  const getPdfInvoice = (
    invoiceId: string,
    copy: boolean,
    compensation: boolean,
    locale: string,
    advance: boolean,
  ) => {
    const invoiceCommand = {
      invoiceId,
      copy,
      compensation,
      locale,
      advance,
    } as CreateBookingPdf;
    dispatch(getInvoiceDetailsPdfAsync(invoiceCommand));
  };
  const cancelContainerLocal = (containerId: string, reason: string) => {
    setshowLoader(true);
    dispatch(
      cancelContainerAsync({
        containerId,
        reasonOfCancellation: reason,
      } as CancelContainer),
    )
      .then(res => {
        if (res) {
          if (params.bookingId !== 'search') {
            dispatch(getBookingContainerDetailsAsync(params.bookingId));
          }
        }
      })
      .finally(() => {
        setshowLoader(false);
      });
    setShowDeleteContainerModal(false);
  };
  const generateItemKey = function (Index: number, Text: string) {
    return `${Index.toString()}-${encodeURI(Text.substring(0, 12))}`;
  };

  function getBookingTypeTranslation(
    bookingType: BookingType,
    locale: 'en' | 'fi',
  ): string {
    return bookingTypeTranslations[locale][bookingType] || 'Unknown';
  }

  useEffect(() => {
    // Get the query string
    const queryString = state.search;
    // Parse the query parameters
    const queryParams = new URLSearchParams(queryString);

    // Get specific parameters
    const paramValue = queryParams.get('bookingType');
    if (
      paramValue !== null &&
      bookingContainer !== null &&
      bookingContainer.bookingResponses != null
    ) {
      const edited = bookingContainer.bookingResponses.find(
        item => item.id.toLowerCase() === params.bookingId.toLowerCase(),
      );
      if (edited != null && edited.isCancelled !== true) {
        const storedBooking = {
          ...edited,
          bookingType: Number.parseInt(paramValue, 10),
        };
        setBookingTypeParam(Number.parseInt(paramValue, 10));
      }
      // Payer email state is handled in a dedicated effect to avoid duplicate state updates
    }
  }, [state.search, bookingContainer]);

  const isTravelAgencyUser = useAppSelector(checkRole([ROLES.TravelAgency]));
  // console.log(`TravelAgency käyttäjä: ${isTravelAgencyUser}`);
  function isProductEligibleForOwnerMaintainer(
    bookingOrderItem: BookingDetailsOrderItem,
  ): boolean {
    const itemName = bookingOrderItem.itemName.toLowerCase();

    if (
      itemName.includes('muutoskulu') ||
      itemName.includes('fee for changes') ||
      itemName.includes('if peruutus') ||
      itemName.includes('if cancel')
    ) {
      return false;
    }

    return true;
  }
  const getOwnersMaintainersForBooking = useCallback(
    (bookingResponse: BookingDetailsData) => {
      const ownersMap: Record<
        string,
        { ownerName: string; emailAddress: string }
      > = {};
      const maintRoles: AccommodationAccountRoleDto[] = [];
      const maintainersList: CareTakerItem[] = [];

      if (bookingResponse?.bookingOrderItems) {
        bookingResponse.bookingOrderItems.forEach(bookingOrderItem => {
          if (bookingOrderItem.realOwnerAccountId != null) {
            if (
              !(bookingOrderItem.realOwnerAccountId in ownersMap) &&
              isProductEligibleForOwnerMaintainer(bookingOrderItem)
            ) {
              let emailAddresses = bookingOrderItem.realOwnerBookingNoteEmail;
              if (bookingOrderItem.accommodationAccountRoles != null) {
                const roleEmailAddresses =
                  bookingOrderItem.accommodationAccountRoles
                    ?.find(
                      o =>
                        o.accountId?.toLowerCase() ===
                        bookingOrderItem.realOwnerAccountId?.toLowerCase(),
                    )
                    ?.emails?.filter(email => email.isBookingNoteEmail)
                    .map(email => email.emailAddress)
                    .join('; ');
                if (roleEmailAddresses != null && roleEmailAddresses !== '') {
                  emailAddresses = roleEmailAddresses;
                }
              }
              ownersMap[bookingOrderItem.realOwnerAccountId] = {
                ownerName: bookingOrderItem.realOwnerName,
                emailAddress: emailAddresses,
              };
            }
          } else if (
            bookingOrderItem.ownerAccountId != null &&
            !(bookingOrderItem.ownerAccountId in ownersMap)
          ) {
            if (isProductEligibleForOwnerMaintainer(bookingOrderItem)) {
              let emailAddresses = bookingOrderItem.ownerBookingNoteEmail;
              if (bookingOrderItem.accommodationAccountRoles != null) {
                const roleEmailAddresses =
                  bookingOrderItem.accommodationAccountRoles
                    ?.find(
                      o =>
                        o.accountId?.toLowerCase() ===
                        bookingOrderItem.ownerAccountId?.toLowerCase(),
                    )
                    ?.emails?.filter(email => email.isBookingNoteEmail)
                    .map(email => email.emailAddress)
                    .join('; ');
                if (roleEmailAddresses != null && roleEmailAddresses !== '') {
                  emailAddresses = roleEmailAddresses;
                }
              }
              ownersMap[bookingOrderItem.ownerAccountId] = {
                ownerName: bookingOrderItem.ownerName,
                emailAddress: emailAddresses,
              };
            }
          }

          if (bookingResponse.isAddOnBooking) {
            if (isProductEligibleForOwnerMaintainer(bookingOrderItem)) {
              bookingOrderItem.careTakers.forEach(maintainer => {
                maintainersList.push(maintainer);
              });
            }
          }

          if (bookingOrderItem.accommodationAccountRoles != null) {
            bookingOrderItem.accommodationAccountRoles.forEach(accountRole => {
              if (
                accountRole.roleEnum !== 'OWNER_ROLE_TYPE' &&
                isProductEligibleForOwnerMaintainer(bookingOrderItem)
              ) {
                maintRoles.push(accountRole);
              }
            });
          }
        });
      }

      return {
        bookingOwners: ownersMap,
        bookingMaintainers: maintainersList,
        bookingMaintainersAsRoles: maintRoles,
      } as const;
    },
    [],
  );
  function setInBasketAndNavigate(): void {
    dispatch(setBasketContainer(bookingContainer));
    history('/bookings/search-accommodations');
  }
  function editBooking(index): void {
    if (
      bookingContainer.bookingResponses != null &&
      bookingContainer.bookingResponses[index] != null
    ) {
      if (
        bookingContainer.bookingResponses[index].externalBookingIdsDto &&
        bookingContainer.bookingResponses[index].externalBookingIdsDto
          .externalBookingSource &&
        bookingContainer.bookingResponses[index].externalBookingIdsDto
          .externalBookingSource === 'TRAVEL_AGENCY'
      ) {
        if (
          bookingContainer.bookingResponses[index] != null &&
          bookingContainer.bookingResponses[index].travelAgencyData !== null &&
          bookingContainer.bookingResponses[index].travelAgencyData
            .finalClientFirstName != null &&
          bookingContainer.bookingResponses[index].travelAgencyData
            .finalClientFirstName !== '' &&
          bookingContainer.bookingResponses[index].travelAgencyData
            .finalClientCountry !== null &&
          bookingContainer.bookingResponses[index].travelAgencyData
            .finalClientCountry !== '00000000-0000-0000-0000-000000000000'
        ) {
          const storedBooking = {
            ...bookingContainer.bookingResponses[index],
            travelAgencyData: {
              finalClientFirstName:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientFirstName ?? '',
              finalClientLastName:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientLastName ?? '',
              finalClientCompany:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientCompany ?? '',
              finalClientStreetAddress:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientStreetAddress ?? '',
              finalClientZipCode:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientZipCode ?? '',
              finalClientCity:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientCity ?? '',
              finalClientCountry:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientCountry ?? '',
              finalClientEmail:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientEmail ?? '',
              finalClientPhone:
                bookingContainer.bookingResponses[index].travelAgencyData
                  .finalClientPhone ?? '',
            } as TravelAgencyData,
          };
          const container = {
            bookingContainerId: bookingContainer.id ?? '',
            bookingContainerStatus: 0,
            bookings: [storedBooking],
            acceptBookingTerms: false,
            acceptCancelInsuranceTerms: false,
            acceptMarketing: false,
          } as BookingContainerUpdateItem;
          dispatch(updateBookingContainerAsync(container)).then(res => {
            if (
              res.payload.isSuccess === true &&
              res.payload.result.bookingResponses
            ) {
              setBookingContainer(res.payload.result);
              // Use the updated booking data from the API response
              const updatedBooking = res.payload.result.bookingResponses.find(
                booking => booking.id === storedBooking.id,
              );
              if (updatedBooking) {
                setEditedBooking(updatedBooking);
                if (
                  updatedBooking.bookingType === 3 ||
                  updatedBooking.bookingType === 4 ||
                  updatedBooking.bookingType === 9 ||
                  updatedBooking.bookingType === 10
                ) {
                  setbackLinkUrl('/?tab=tab1');
                }
                setShowEditBookingModal(true);
              }
            } else {
              const findItem = res.payload.result.bookingResponses.filter(
                i => i.id === editedBooking.id,
              );
              console.debug(
                'Error updating booking container',
                res.payload.result.errors,
              );
              if (findItem != null && findItem.length > 0) {
                setEditedBooking(findItem[0]);
                dispatch(setBookingContainer(res.payload.result));
              }
              dispatch(
                addStatusToast({
                  Message:
                    t(
                      `travelAgencyBookingModal:validation:errors.${
                        res.payload.result.errors[0]
                      }`,
                    ) ?? t('travelAgencyBookingModal:validation:errors.Virhe'),
                  IsFailed: true,
                  Title: 'Virhe',
                }),
              );
            }
          });
        } else {
          const storedBooking = {
            ...bookingContainer.bookingResponses[index],
            travelAgencyData: null,
          };
          const container = {
            bookingContainerId: bookingContainer.id ?? '',
            bookingContainerStatus: 0,
            bookings: [storedBooking],
            acceptBookingTerms: false,
            acceptCancelInsuranceTerms: false,
            acceptMarketing: false,
          } as BookingContainerUpdateItem;
          dispatch(updateBookingContainerAsync(container)).then(res => {
            if (
              res.payload.isSuccess === true &&
              res.payload.result.bookingResponses
            ) {
              setBookingContainer(res.payload.result);
              // Use the updated booking data from the API response
              const updatedBooking = res.payload.result.bookingResponses.find(
                booking => booking.id === storedBooking.id,
              );
              if (updatedBooking) {
                setEditedBooking(updatedBooking);
                if (
                  updatedBooking.bookingType === 3 ||
                  updatedBooking.bookingType === 4 ||
                  updatedBooking.bookingType === 9 ||
                  updatedBooking.bookingType === 10
                ) {
                  setbackLinkUrl('/?tab=tab1');
                }
                setShowEditBookingModal(true);
              }
            } else {
              const findItem = res.payload.result.bookingResponses.filter(
                i => i.id === editedBooking.id,
              );
              console.debug(
                'Error updating booking container',
                res.payload.result.errors,
              );
              if (findItem != null && findItem.length > 0) {
                setEditedBooking(findItem[0]);
                dispatch(setBookingContainer(res.payload.result));
              }
              dispatch(
                addStatusToast({
                  Message: res.payload.result.errors[0] ?? 'Virhe',
                  IsFailed: true,
                  Title: 'Virhe',
                }),
              );
            }
          });
        }
      } else {
        const container = {
          bookingContainerId: bookingContainer.id ?? '',
          bookingContainerStatus: 0,
          bookings: [bookingContainer.bookingResponses[index]],
          acceptBookingTerms: false,
          acceptCancelInsuranceTerms: false,
          acceptMarketing: false,
        } as BookingContainerUpdateItem;
        if (
          bookingContainer.bookingResponses &&
          bookingContainer.bookingResponses.length > 1
        ) {
          // console.debug('refresh1');
          // setRefreshBookingModal(true);
        }
        dispatch(updateBookingContainerAsync(container)).then(res => {
          if (
            res.payload.isSuccess === true &&
            res.payload.result.bookingResponses
          ) {
            // Use the updated booking data from the API response
            setBookingContainer(res.payload.result);
            const updatedBooking = res.payload.result.bookingResponses.find(
              booking =>
                booking.id === bookingContainer.bookingResponses[index].id,
            );
            if (updatedBooking) {
              setEditedBooking(updatedBooking);

              if (
                updatedBooking.bookingType === 3 ||
                updatedBooking.bookingType === 4 ||
                updatedBooking.bookingType === 9 ||
                updatedBooking.bookingType === 10
              ) {
                setbackLinkUrl('/?tab=tab1');
              }
              setShowEditBookingModal(true);
            }
          } else {
            const findItem = res.payload.result.bookingResponses.filter(
              i => i.id === editedBooking.id,
            );
            console.debug(
              'Error updating booking container',
              res.payload.result.errors,
            );
            if (findItem != null && findItem.length > 0) {
              setEditedBooking(findItem[0]);
              dispatch(setBookingContainer(res.payload.result));
            }
            dispatch(
              addStatusToast({
                Message:
                  t(
                    `travelAgencyBookingModal:validation:errors.${
                      res.payload.result.errors[0]
                    }`,
                  ) ?? t('travelAgencyBookingModal:validation:errors.Virhe'),
                IsFailed: true,
                Title: 'Virhe',
              }),
            );
          }
        });
      }
    }
  }
  function showCancelModal(item): void {
    setShowDeleteModal(true);
    setEditedBooking(item);
  }

  // function removeFromBasket(removedItem) {
  //   const removeData = {
  //     bookingId: removedItem.id,
  //     bookingContainerId: bookingContainer.id,
  //     orderItemId: '00000000-0000-0000-0000-000000000000',
  //   } as RemoveFromBookingContainerItem;
  //
  //   dispatch(removeFromBookingContainerAsync(removeData)).then(res => {
  //     if (res.payload.isSuccess === true) {
  //       console.debug('removeOk:', res.payload.result);
  //       dispatch(setBasketContainer(res.payload.result));
  //       const firstBookingResponse = bookingContainer?.bookingResponses?.find(
  //         () => true,
  //       );
  //       console.debug('findItem:', firstBookingResponse);
  //       if (firstBookingResponse) {
  //         console.debug('findItemOK:', firstBookingResponse);
  //         history(`/bookings/${firstBookingResponse.id}`);
  //       } else {
  //         console.debug('findItemNOK:', null);
  //         setEditedBooking(null);
  //       }
  //     }
  //   });
  // }

  function showAdditionalModal(item): void {
    setShowAdditionalBookingModal(true);
    setEditedBooking(item);
  }

  function showCancelContainerModal(item): void {
    setShowDeleteContainerModal(true);
  }

  function sendClientBookingConfirmation(bookingId: string): void {
    if (!isValidEmail(clientBookingConfirmationToSentEmailAddress)) {
      return;
    }
    const sendbookingConfirmationEmailRequest: sendClientBookingConfirmationRequest =
      {
        bookingId,
        sendToEmail: clientBookingConfirmationToSentEmailAddress,
        locale: editedBooking.invoicingLanguageLocale,
      };

    dispatch(
      sendBookingConfirmationEmail(sendbookingConfirmationEmailRequest),
    ).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(getBookingsMessageEventsAsync(params.bookingId));
        alert(t('bookingDetails:bookingConfirmationMessageEventSent'));
      }
    });
  }
  function sendClientCancellationConfirmation(bookingId: string): void {
    if (!isValidEmails(clientBookingCancellationToSentEmailAddress)) {
      return;
    }
    const sendClientCancellationMessageRequesti: sendClientCancellationMessageRequest =
      {
        bookingId,
        sendToEmail: clientBookingCancellationToSentEmailAddress,
      };

    dispatch(
      sendBookingCancellationMessageEmail(
        sendClientCancellationMessageRequesti,
      ),
    ).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(getBookingsMessageEventsAsync(params.bookingId));
        alert(t('bookingDetails:bookingCancellationMessageEventSent'));
      }
    });
  }
  function sendClientAnyPhaseConfirmation(
    phase: ClientMessagePhase,
    bookingId: string,
  ): void {
    let email = '';
    switch (phase) {
      case 1:
        email = clientBookingPhase1ToSentEmailAddress;
        break;
      case 2:
        email = clientBookingPhase2ToSentEmailAddress;
        break;
      case 3:
        email = clientBookingPhase3ToSentEmailAddress;
        break;
      default:
        console.log('Unknown phase message type!');
        alert(t('bookingDetails:unknownMessageType'));
        return;
    }

    if (!isValidEmails(email)) {
      return;
    }
    const sendClientPhaseMessageRequesti: sendClientPhaseMessageRequest = {
      bookingId,
      sendToEmail: email,
      phase,
    };

    dispatch(sendBookingPhaseMessageEmail(sendClientPhaseMessageRequesti)).then(
      res => {
        if (res.meta.requestStatus === 'fulfilled') {
          dispatch(getBookingsMessageEventsAsync(params.bookingId));
          alert(t('bookingDetails:phaseMessageEventSent', { phase }));
        }
      },
    );
  }

  function refreshBookingMessageEvents() {
    dispatch(getBookingsMessageEventsAsync(params.bookingId));
  }

  function sendClientBillsFromBooking(bookingId: string): void {
    let email = '';
    email = clientBookingBillsToSentEmailAddress;

    if (!isValidEmail(email)) {
      return;
    }
    const sendClientBillsMessageRequesti: sendClientBillsMessageRequest = {
      bookingId,
      sendToEmail: email,
    };

    dispatch(sendBookingBillsMessageEmail(sendClientBillsMessageRequesti)).then(
      res => {
        if (res.meta.requestStatus === 'fulfilled') {
          dispatch(getBookingsMessageEventsAsync(params.bookingId));
          alert(t('bookingDetails:invoicesSentToEmail', { email }));
        }
      },
    );
  }

  const sendOwnerBookingConfirmation = (
    ownerAccountId: string,
    emailAddress: string,
    bookingId: string,
  ) => {
    if (
      emailAddress === null || // disable if empty or undefined
      !isValidEmails(emailAddress)
    ) {
      return;
    }
    /*
    if (!isValidEmail(emailAddress)) {
      return;
    }
     */
    const sendbookingConfirmationEmailRequest: sendOwnerBookingConfirmationRequest =
      {
        bookingId,
        ownerId: ownerAccountId,
        sendToEmail: emailAddress,
      };

    dispatch(
      sendBookingConfirmationEmailToOwner(sendbookingConfirmationEmailRequest),
    ).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(getBookingsMessageEventsAsync(params.bookingId));
        alert(t('bookingDetails:ownerBookingConfirmationMessageEventSent'));
      }
    });
  };

  const sendMaintainerBookingConfirmation = (
    maintainerAccountId: string,
    emailAddress: string,
    bookingId: string,
  ) => {
    if (
      emailAddress === null || // disable if empty or undefined
      !isValidEmails(emailAddress)
    ) {
      return;
    }
    const sendBookingConfirmationEmailRequest: sendMaintainerBookingConfirmationRequest =
      {
        bookingId,
        maintainerId: maintainerAccountId,
        sendToEmail: emailAddress,
      };
    dispatch(
      sendBookingConfirmationEmailToMaintainer(
        sendBookingConfirmationEmailRequest,
      ),
    ).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        dispatch(getBookingsMessageEventsAsync(params.bookingId));
        alert(t('bookingDetails:caretakerBookingConfirmationMessageEventSent'));
      }
    });
  };

  const getStatus = () => {
    if (
      bookingContainer !== undefined &&
      bookingContainer.id !== '00000000-0000-0000-0000-000000000000' &&
      bookingContainer.bookingResponses.length > 1
    )
      if (bookingContainer.bookingContainerStatus === 1) {
        return t('bookingDetails:status.confirmed');
      }
    if (bookingContainer.bookingContainerStatus === 2) {
      return t('bookingDetails:status.completed');
    }
    if (bookingContainer.bookingContainerStatus === 3) {
      return t('bookingDetails:status.cancelled');
    }
    return t('bookingDetails:status.draft');
  };

  useEffect(() => {
    if (bookingContainer !== null && bookingContainer !== undefined) {
      console.log('setWebshopCustomer bdetails null:');
      dispatch(setWebShopCustomerState(null));
      if (params.customerId !== undefined && params.bookingId !== undefined) {
        const edited = bookingContainer.bookingResponses.find(
          item => item.id.toLowerCase() === params.bookingId.toLowerCase(),
        );
        if (
          edited != null &&
          edited.payerId != null &&
          edited.payerId.toLowerCase() !== params.customerId.toLowerCase() &&
          edited.isCancelled !== true
        ) {
          const storedBooking = {
            ...edited,
            payerId: params.customerId,
          };
          setEditedBooking(storedBooking);
        } else if (
          edited !== undefined &&
          edited.isCancelled &&
          edited.isCancelled === true
        ) {
          history(`/bookings/${edited.id}`);
        } else if (edited) {
          setEditedBooking(edited);
          setShowEditBookingModal(false);
          history(`/bookings/${edited.id}`);
        }
        if (edited != null && edited.payerId === null) {
          const storedBooking = {
            ...edited,
            payerId: params.customerId,
          };
          setEditedBooking(storedBooking);
        }
        if (edited !== undefined && edited.isCancelled === true) {
          setShowEditBookingModal(false);
        } else {
          setShowEditBookingModal(true);
        }
      }
    }
  }, [bookingContainer]);
  const handleBookingCustomerNoteChange = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.customerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      if (bookingItemNote) {
        const editedNote = { ...bookingItemNote, noteText: value };
        // now update the booking item
        const bookingItemIndex = bookingItem.customerBookingNotes.findIndex(
          ni => ni.id.toLowerCase() === noteId.toLowerCase(),
        );
        const newBookingItem = {
          ...bookingItem,
          customerBookingNotes: [
            ...bookingItem.customerBookingNotes.slice(0, bookingItemIndex),
            editedNote,
            ...bookingItem.customerBookingNotes.slice(bookingItemIndex + 1),
          ],
        };
        setEditedBooking(newBookingItem);
        // and newBookingItem to bookingContainer
        const bookingContainerIndex =
          bookingContainer.bookingResponses.findIndex(
            bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
          );
        const newBookingContainer = {
          ...bookingContainer,
          bookingResponses: [
            ...bookingContainer.bookingResponses.slice(
              0,
              bookingContainerIndex,
            ),
            newBookingItem,
            ...bookingContainer.bookingResponses.slice(
              bookingContainerIndex + 1,
            ),
          ],
        };
        dispatch(setBookingContainer(newBookingContainer));
      }
    }
  };
  const handleBookingOwnerNoteChange = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.ownerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      if (bookingItemNote) {
        const editedNote = { ...bookingItemNote, noteText: value };
        // now update the booking item
        const bookingItemIndex = bookingItem.ownerBookingNotes.findIndex(
          ni => ni.id.toLowerCase() === noteId.toLowerCase(),
        );
        const newBookingItem = {
          ...bookingItem,
          ownerBookingNotes: [
            ...bookingItem.ownerBookingNotes.slice(0, bookingItemIndex),
            editedNote,
            ...bookingItem.ownerBookingNotes.slice(bookingItemIndex + 1),
          ],
        };
        setEditedBooking(newBookingItem);
        // and newBookingItem to bookingContainer
        const bookingContainerIndex =
          bookingContainer.bookingResponses.findIndex(
            bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
          );
        const newBookingContainer = {
          ...bookingContainer,
          bookingResponses: [
            ...bookingContainer.bookingResponses.slice(
              0,
              bookingContainerIndex,
            ),
            newBookingItem,
            ...bookingContainer.bookingResponses.slice(
              bookingContainerIndex + 1,
            ),
          ],
        };
        dispatch(setBookingContainer(newBookingContainer));
      }
    }
  };
  const handleBookingOfficerNoteChange = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.officerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      if (bookingItemNote) {
        const editedNote = { ...bookingItemNote, noteText: value };
        // now update the booking item
        const bookingItemIndex = bookingItem.officerBookingNotes.findIndex(
          ni => ni.id.toLowerCase() === noteId.toLowerCase(),
        );
        const newBookingItem = {
          ...bookingItem,
          officerBookingNotes: [
            ...bookingItem.officerBookingNotes.slice(0, bookingItemIndex),
            editedNote,
            ...bookingItem.officerBookingNotes.slice(bookingItemIndex + 1),
          ],
        };

        setEditedBooking(newBookingItem);
        // and newBookingItem to bookingContainer
        const bookingContainerIndex =
          bookingContainer.bookingResponses.findIndex(
            bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
          );
        const newBookingContainer = {
          ...bookingContainer,
          bookingResponses: [
            ...bookingContainer.bookingResponses.slice(
              0,
              bookingContainerIndex,
            ),
            newBookingItem,
            ...bookingContainer.bookingResponses.slice(
              bookingContainerIndex + 1,
            ),
          ],
        };

        dispatch(setBookingContainer(newBookingContainer));
      }
    }
  };
  const handleBookingCustomerNoteSave = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.customerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      if (bookingItemNote) {
        // now update the booking item
        const bookingItemIndex = bookingItem.customerBookingNotes.findIndex(
          ni => ni.id.toLowerCase() === noteId.toLowerCase(),
        );
        const noteUpdateData: UpdateBookingNoteItem = {
          bookingId: bookingItem.id,
          notesType: 1,
          bookingNote: {
            noteText: bookingItemNote.noteText,
            id: bookingItemNote.id,
          },
        };
        dispatch(updateBookingNoteAsync(noteUpdateData)).then(res => {
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(getBookingContainerDetailsAsync(bookingItem.id));
          }
        });
      }
    }
    setEditCustomerNote(false);
  };
  const handleBookingOwnerNoteSave = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.ownerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      if (bookingItemNote) {
        // now update the booking item
        const bookingItemIndex = bookingItem.ownerBookingNotes.findIndex(
          ni => ni.id.toLowerCase() === noteId.toLowerCase(),
        );
        const noteUpdateData: UpdateBookingNoteItem = {
          bookingId: bookingItem.id,
          notesType: 2,
          bookingNote: {
            noteText: bookingItemNote.noteText,
            id: bookingItemNote.id,
          },
        };
        dispatch(updateBookingNoteAsync(noteUpdateData)).then(res => {
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(getBookingContainerDetailsAsync(bookingItem.id));
          }
        });
      }
    }
    setEditOwnerNote(false);
  };
  const handleBookingOfficerNoteSave = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.officerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      if (bookingItemNote) {
        // now update the booking item
        const bookingItemIndex = bookingItem.officerBookingNotes.findIndex(
          ni => ni.id.toLowerCase() === noteId.toLowerCase(),
        );
        const noteUpdateData: UpdateBookingNoteItem = {
          bookingId: bookingItem.id,
          notesType: 3,
          bookingNote: {
            noteText: bookingItemNote.noteText,
            id: bookingItemNote.id,
          },
        };
        dispatch(updateBookingNoteAsync(noteUpdateData)).then(res => {
          if (res.meta.requestStatus === 'fulfilled') {
            dispatch(getBookingContainerDetailsAsync(bookingItem.id));
          }
        });
      }
    }
    setEditOfficerNote(false);
  };
  const handleAddCustomerBookingNote = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.customerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      const newbookingNote: BookingDetailsNoteItem = {
        noteText: '',
        id: '00000000-0000-0000-0000-000000000000',
      };
      const newBookingNoteItems = [
        ...bookingItem.customerBookingNotes,
        newbookingNote,
      ];
      const updatedBookingItem = {
        ...bookingItem,
        customerBookingNotes: newBookingNoteItems,
      };
      const bookingContainerIndex = bookingContainer.bookingResponses.findIndex(
        bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
      );
      const newBookingContainer = {
        ...bookingContainer,
        bookingResponses: [
          ...bookingContainer.bookingResponses.slice(0, bookingContainerIndex),
          updatedBookingItem,
          ...bookingContainer.bookingResponses.slice(bookingContainerIndex + 1),
        ],
      };

      dispatch(setBookingContainer(newBookingContainer));
    }
  };
  const handleAddOwnerBookingNote = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.ownerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      const newbookingNote: BookingDetailsNoteItem = {
        noteText: '',
        id: '00000000-0000-0000-0000-000000000000',
      };
      const newBookingNoteItems = [
        ...bookingItem.ownerBookingNotes,
        newbookingNote,
      ];
      const updatedBookingItem = {
        ...bookingItem,
        ownerBookingNotes: newBookingNoteItems,
      };
      const bookingContainerIndex = bookingContainer.bookingResponses.findIndex(
        bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
      );
      const newBookingContainer = {
        ...bookingContainer,
        bookingResponses: [
          ...bookingContainer.bookingResponses.slice(0, bookingContainerIndex),
          updatedBookingItem,
          ...bookingContainer.bookingResponses.slice(bookingContainerIndex + 1),
        ],
      };

      dispatch(setBookingContainer(newBookingContainer));
    }
  };
  const showPhase123EmailBoxes = (booking: BookingDetailsData) => {
    /* Tarkistus  että bookingilla on payerId ja ei ole peruttu ja ei ole lisävaraus
    muuten ei voida mitään viestiä lähettää */
    // Myöskään suomulle ei näytetä vaihe viesti juttuja
    console.debug('showPhase123EmailBoxes:', booking.crossSiteBooking);
    if (
      !booking.isCancelled &&
      booking.payerId != null &&
      !booking.isAddOnBooking &&
      booking.crossSiteBooking === false &&
      booking.sellerSiteId !== '9ECB7F14-13A6-4F29-A3EE-0FCF1977DAEF'
    ) {
      console.debug('showPhase123EmailBoxes ret: true');
      return true;
    }
    console.debug('showPhase123EmailBoxes ret: false');
    return false;
  };
  const showEmailConfirmationSuomu = (booking: BookingDetailsData) => {
    /* Tarkistus  että bookingilla on payerId ja ei ole peruttu ja ei ole lisävaraus
    muuten ei voida mitään viestiä lähettää */
    // Vain suomulle näytetään tämä
    // console.log(`booking sellersite: ${booking.sellerSiteId}`);
    if (
      !booking.isCancelled &&
      booking.payerId != null &&
      !booking.isAddOnBooking &&
      booking.sellerSiteId === '9ECB7F14-13A6-4F29-A3EE-0FCF1977DAEF'
    ) {
      console.log('showing customer email');
      return true;
    }
    return false;
  };
  const showClientCancellationEmailBox = (booking: BookingDetailsData) => {
    /* Tarkistus  että bookingilla on payerId ja on peruttu ja ei ole lisävaraus
    muuten ei voida mitään viestiä lähettää */
    if (
      booking.isCancelled &&
      booking.payerId != null &&
      !booking.isAddOnBooking
      // && booking.sellerSiteId !== '9ECB7F14-13A6-4F29-A3EE-0FCF1977DAEF'
    ) {
      return true;
    }
    return false;
  };
  const showOwnerCaretakerEmailBoxes = () => {
    return true;
  };
  const handleAddOfficerBookingNote = (event, bookingId, noteId) => {
    const { value } = event.target;
    const bookingItem = bookingContainer.bookingResponses.find(
      bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
    );
    if (bookingItem) {
      const bookingItemNote = bookingItem.officerBookingNotes.find(
        noi => noi.id.toLowerCase() === noteId.toLowerCase(),
      );
      const newbookingNote: BookingDetailsNoteItem = {
        noteText: '',
        id: '00000000-0000-0000-0000-000000000000',
      };
      const newBookingNoteItems = [
        ...bookingItem.officerBookingNotes,
        newbookingNote,
      ];
      const updatedBookingItem = {
        ...bookingItem,
        officerBookingNotes: newBookingNoteItems,
      };
      const bookingContainerIndex = bookingContainer.bookingResponses.findIndex(
        bo => bo.id.toLowerCase() === bookingId.toLowerCase(),
      );
      const newBookingContainer = {
        ...bookingContainer,
        bookingResponses: [
          ...bookingContainer.bookingResponses.slice(0, bookingContainerIndex),
          updatedBookingItem,
          ...bookingContainer.bookingResponses.slice(bookingContainerIndex + 1),
        ],
      };

      dispatch(setBookingContainer(newBookingContainer));
    }
  };
  const bookingItems =
    bookingContainer?.bookingResponses.length > 1
      ? (bookingContainer?.bookingResponses || [])
          .filter(Boolean)
          .map((item, index) => (
            <Accordion
              key={`accordion-${item.id}`}
              style={{ marginBottom: '1px' }}
            >
              <Accordion.Item
                key={`accordion-item-${item.id}`}
                eventKey={`booking-${item.id}`}
              >
                <Accordion.Header>
                  <div className="d-md-flex align-items-center flex-grow-1">
                    <span
                      className="d-block col-md-4 ps-md-0"
                      style={
                        item.isCancelled
                          ? { textDecoration: 'line-through', color: '#999' }
                          : {}
                      }
                    >
                      {item.bookingNumber}{' '}
                      {
                        item.bookingOrderItems.find(i => i.orderItemType === 0)
                          ?.itemName
                      }{' '}
                      {item.isCancelled && <Badge bg="danger">Peruttu</Badge>}
                    </span>
                    <span className="d-block col-md-4 ps-md-0">
                      {new Date(
                        Date.parse(
                          item.bookingOrderItems.find(
                            i => i.orderItemType !== null,
                          )?.startDateTime,
                        ),
                      ).toLocaleString('fi-FI', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                      -
                      {new Date(
                        Date.parse(
                          item.bookingOrderItems.find(
                            i => i.orderItemType !== null,
                          )?.endDateTime,
                        ),
                      ).toLocaleString('fi-FI', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}{' '}
                    </span>
                    <span className="d-block col-md-4 ps-md-0">
                      {getBookingTypeTranslation(
                        item.bookingType,
                        currentLanguage,
                      )}
                      {': '}
                      {item.bookingType === 0 &&
                        item.preliminaryBookingDueDate && (
                          <span>
                            {t('bookingDetails:preliminaryBookingValid', {
                              date: formatUtcDateTimeToFinnish(
                                item.preliminaryBookingDueDate,
                              ),
                            })}
                          </span>
                        )}
                      {item.externalBookingIdsDto &&
                        item.externalBookingIdsDto.externalBookingSource &&
                        item.externalBookingIdsDto.externalBookingSource ===
                          'TRAVEL_AGENCY' && (
                          <span className="small">
                            {t('bookingDetails:travelAgencyBooking')}
                          </span>
                        )}
                    </span>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="pt-8 pb-40 px-16 px-md-24">
                  <React.Fragment
                    key={generateItemKey(index, item.id.toString())}
                  >
                    <div className="d-flex flex-column flex-lg-row gap-16 justify-content-end mb-32">
                      {!isTravelAgencyUser &&
                        item.isCancelled === false &&
                        item.isAllowedToEdit &&
                        item.bookingType !== 1 &&
                        item.bookingType !== 2 &&
                        item.bookingType !== 4 &&
                        item.bookingType !== 6 &&
                        item.bookingType !== 8 &&
                        item.bookingType !== 10 && (
                          <Button
                            onClick={() => editBooking(index)}
                            variant="outline-blue"
                            disabled={item.isCancelled}
                          >
                            {t('common:buttons.modify')}
                          </Button>
                        )}
                      {!isTravelAgencyUser &&
                        item.isCancelled === false &&
                        item.isAllowedToEdit &&
                        item.bookingOrderItems.find(
                          oi => oi.orderItemType === 0,
                        ) &&
                        (item.bookingType === 1 ||
                          item.bookingType === 2 ||
                          item.bookingType === 4 ||
                          item.bookingType === 6 ||
                          item.bookingType === 8 ||
                          item.bookingType === 10) && (
                          <Button
                            onClick={
                              item.isCancelled
                                ? null
                                : () => showAdditionalModal(item)
                            }
                            disabled={
                              !item.isAllowedToEdit ||
                              !item.isAllowedToCancel ||
                              item.isCancelled
                            }
                            variant="dark"
                            className="text-nowrap"
                          >
                            {t(
                              'bookingDetails:bookingInfo.makeAdditionalBooking',
                            )}
                          </Button>
                        )}
                      {!isTravelAgencyUser &&
                        item.isCancelled === false &&
                        item.isAllowedToCancel &&
                        item.isAllowedToEdit &&
                        (item.bookingType === 0 ||
                          item.bookingType === 1 ||
                          item.bookingType === 2 ||
                          item.bookingType === 4 ||
                          item.bookingType === 5 ||
                          item.bookingType === 13 ||
                          item.bookingType === 6 ||
                          item.bookingType === 8 ||
                          item.bookingType === 3 ||
                          item.bookingType === 9 ||
                          item.bookingType === 10) && (
                          <Button
                            onClick={
                              item.isCancelled
                                ? null
                                : () =>
                                    detachBookingLocal(
                                      item.id,
                                      item.bookingContainerId,
                                    )
                            }
                            disabled={
                              !item.isAllowedToEdit ||
                              !item.isAllowedToCancel ||
                              item.isCancelled
                            }
                            variant="outline-blue"
                            className="text-nowrap"
                          >
                            {t(
                              'bookingDetails:bookingInfo.moveToSingleBooking',
                            )}
                          </Button>
                        )}
                      {!isTravelAgencyUser &&
                        item.isCancelled === false &&
                        item.isAllowedToCancel &&
                        item.isAllowedToEdit &&
                        (item.bookingType === 0 ||
                          item.bookingType === 1 ||
                          item.bookingType === 2 ||
                          item.bookingType === 4 ||
                          item.bookingType === 5 ||
                          item.bookingType === 13 ||
                          item.bookingType === 6 ||
                          item.bookingType === 8 ||
                          item.bookingType === 3 ||
                          item.bookingType === 9 ||
                          item.bookingType === 13 ||
                          item.bookingType === 10) && (
                          <Button
                            onClick={
                              item.isCancelled
                                ? null
                                : () => showCancelModal(item)
                            }
                            disabled={
                              !item.isAllowedToEdit ||
                              !item.isAllowedToCancel ||
                              item.isCancelled
                            }
                            variant="danger"
                            className="text-nowrap"
                          >
                            {t('bookingDetails:cancellation.cancelBooking')}
                          </Button>
                        )}
                    </div>
                    <BasicBookingData
                      item={item}
                      labels={{
                        bookingNumber: t(
                          'bookingDetails:bookingInfo.bookingNumber',
                        ),
                        keyCode: t('bookingDetails:bookingInfo.keyCode'),
                        bookingType: t(
                          'bookingDetails:bookingInfo.bookingType',
                        ),
                        created: t('bookingDetails:bookingInfo.created'),
                        modifiedConfirmed: t(
                          'bookingDetails:bookingInfo.modifiedConfirmed',
                        ),
                        validUntil: t('bookingDetails:bookingInfo.validUntil'),
                        clerk: t('bookingDetails:bookingInfo.clerk'),
                        travelAgency: t(
                          'bookingDetails:bookingInfo:externalBooking.travelAgency',
                        ),
                        interhome: t(
                          'bookingDetails:bookingInfo:externalBooking.interhome',
                        ),
                        interhomeYllaksenmatkailu: t(
                          'bookingDetails:bookingInfo:externalBooking.interhomeYllaksenmatkailu',
                        ),
                        agent: t(
                          'bookingDetails:bookingInfo:externalBooking.agent',
                        ),
                      }}
                      bookingTypeText={getBookingTypeTranslation(
                        item.bookingType,
                        currentLanguage,
                      )}
                      isTravelAgencyUser={isTravelAgencyUser}
                    />
                    {item.isCancelled && (
                      <Alert variant="danger" className="mb-32">
                        {t('bookingDetails:bookingInfo.bookingIsCancelled')}
                        <div>
                          {t('bookingDetails:bookingInfo.cancelledAt')}:{' '}
                          {new Date(
                            Date.parse(item.cancelledAt ?? ''),
                          ).toLocaleString('fi-FI', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        <div>
                          Peruja:
                          {item.cancellerFirstName ?? ''}
                          {item.cancellerLastName ?? ''}
                        </div>
                      </Alert>
                    )}
                    {!item.isCancelled &&
                      !item.isAddOnBooking &&
                      !(
                        item.externalBookingIdsDto.externalBookingSource ===
                          'TRAVEL_AGENCY' ||
                        item.externalBookingIdsDto.externalBookingSource ===
                          'INTERHOME' ||
                        item.externalBookingIdsDto.externalBookingSource ===
                          'INTERHOME_YLLAKSENMATKAILU'
                      ) &&
                      (item.bookingType === 3 ||
                        item.bookingType === 4 ||
                        item.bookingType === 9 ||
                        item.bookingType === 10) &&
                      item.paymentMethod === null && (
                        <Alert
                          variant="danger"
                          className="mb-32"
                          style={{
                            backgroundColor: '#ff6b6b',
                            fontSize: '16px',
                            fontWeight: '500',
                          }}
                        >
                          {t(
                            'bookingDetails:bookingInfo.incompleteBookingWarning',
                          )}
                        </Alert>
                      )}
                    {(item.bookingType === 3 ||
                      item.bookingType === 4 ||
                      item.bookingType === 9 ||
                      item.bookingType === 10) &&
                      !(
                        item.externalBookingIdsDto &&
                        item.externalBookingIdsDto.externalBookingSource &&
                        (item.externalBookingIdsDto.externalBookingSource ===
                          'TRAVEL_AGENCY' ||
                          item.externalBookingIdsDto.externalBookingSource ===
                            'INTERHOME' ||
                          item.externalBookingIdsDto.externalBookingSource ===
                            'INTERHOME_YLLAKSENMATKAILU')
                      ) &&
                      item.paymentMethod !== null && (
                        <Alert className="mb-32">
                          <div>
                            {t('bookingDetails:bookingInfo.payment.title')}:
                          </div>
                          {item.onlinePaymentCompleted && (
                            <>
                              <div className="mb-32">
                                {t(
                                  'bookingDetails:bookingInfo.payment.onlinePaymentSuccessful',
                                )}
                              </div>
                              {item &&
                                item.advanceBilled &&
                                // With this:
                                item.advanceBilled === true &&
                                item.paymentAmount !== null &&
                                item.paymentAmount > 0 && (
                                  <div>
                                    {t(
                                      'bookingDetails:bookingInfo.payment.fullyPaid',
                                    )}
                                  </div>
                                )}
                              <span>
                                {t(
                                  'bookingDetails:bookingInfo.payment.paidAmount',
                                  { amount: item.paymentAmount },
                                )}
                              </span>
                            </>
                          )}
                          {!item.onlinePaymentCompleted &&
                            item.paymentMethod !== null && (
                              <Alert variant="danger" className="mb-32">
                                {t(
                                  'bookingDetails:bookingInfo.payment.onlinePaymentFailed',
                                )}
                              </Alert>
                            )}
                          {item.paymentMethod && (
                            <div className="mb-32">
                              {t(
                                'bookingDetails:bookingInfo.payment.paymentMethod',
                              )}
                              : {item.paymentMethod}
                            </div>
                          )}
                        </Alert>
                      )}
                    {item.reasonOfCancellation &&
                      item.reasonOfCancellation !== '' && (
                        <Alert variant="primary" className="mb-32">
                          {t('bookingDetails:bookingInfo.cancellationReason', {
                            reason: item.reasonOfCancellation,
                          })}
                        </Alert>
                      )}
                    <hr />
                    <Heading
                      Level={4}
                      Title={t('bookingDetails:bookingInfo.customer.title')}
                    />
                    <div className="d-flex flex-column flex-lg-row gap-24 justify-content-start my-16 mb-32">
                      <div className="d-flex flex-column flex-lg-row gap-8 gap-lg-40">
                        <div>
                          <Row>
                            <Col>
                              <span className="fw-bold small">
                                {t(
                                  'bookingDetails:bookingInfo.customer.subTitle',
                                )}
                              </span>
                              {!isTravelAgencyUser &&
                                item.payerId !== undefined &&
                                item.payerId !== null &&
                                item.canViewCustomer && (
                                  <button
                                    type="submit"
                                    className="btn btn-link booking-link bg-transparent p-0 text-start border-0"
                                    onClick={e => {
                                      e.preventDefault();
                                      window.open(
                                        `/customers/details/${item.payerId}`,
                                        '_blank',
                                        `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                      );
                                    }}
                                  >
                                    {item.payerAccountNumber ?? ''}{' '}
                                    {item.payerFirstName ?? ''}{' '}
                                    {item.payerLastName ?? ''}{' '}
                                  </button>
                                )}
                            </Col>
                            <Col>
                              {isTravelAgencyUser ||
                                (!item.canViewCustomer && (
                                  <span>
                                    {item.payerAccountNumber ?? ''}{' '}
                                    {item.payerFirstName ?? ''}{' '}
                                    {item.payerLastName ?? ''}{' '}
                                  </span>
                                ))}
                              {item != null &&
                                item.webShopCustomer !== null && (
                                  <>
                                    <span className="fw-bold small">
                                      {t(
                                        'bookingDetails:bookingInfo.customer.webshop',
                                      )}
                                      :
                                    </span>
                                    <br />
                                    <div>
                                      {item.webShopCustomer.firstName ?? ''}{' '}
                                      {item.webShopCustomer.lastName ?? ''}{' '}
                                      <br />
                                      {item.webShopCustomer.companyName ??
                                        ''}{' '}
                                      <br />
                                      {item.webShopCustomer.email ?? ''} <br />
                                      {item.webShopCustomer.streetAddress ??
                                        ''}{' '}
                                      <br />
                                      {item.webShopCustomer.postalCode ??
                                        ''}{' '}
                                      {item.webShopCustomer.postCity ?? ''}
                                      <br />
                                      {item.webShopCustomer.mobileNumber ??
                                        ''}{' '}
                                      <br />
                                      {item.webShopCustomer.phoneNumber ??
                                        ''}{' '}
                                    </div>
                                  </>
                                )}
                              {item != null &&
                                item.travelAgencyData !== null &&
                                item.travelAgencyData.finalClientFirstName !==
                                  null &&
                                item.travelAgencyData.finalClientFirstName !==
                                  '' && (
                                  <>
                                    <span className="fw-bold small">
                                      Matkatoimisto loppuasiakas:
                                    </span>
                                    <br />
                                    <div>
                                      {item.travelAgencyData
                                        .finalClientFirstName ?? ''}{' '}
                                      {item.travelAgencyData
                                        .finalClientLastName ?? ''}{' '}
                                      <br />
                                      {item.travelAgencyData
                                        .finalClientCompany && (
                                        <>
                                          {
                                            item.travelAgencyData
                                              .finalClientCompany
                                          }{' '}
                                          <br />
                                        </>
                                      )}
                                      {
                                        item.travelAgencyData
                                          .finalClientStreetAddress
                                      }{' '}
                                      <br />
                                      {
                                        item.travelAgencyData.finalClientZipCode
                                      }{' '}
                                      {item.travelAgencyData.finalClientCity}{' '}
                                      <br />
                                      {countries != null && (
                                        <>
                                          {countries.find(
                                            country =>
                                              country.id.toLowerCase() ===
                                              item.travelAgencyData.finalClientCountry.toLowerCase(),
                                          )?.name ?? 'Ei valittua maata'}{' '}
                                          {t(
                                            'bookingDetails:bookingInfo.customer.noCountrySelected',
                                          )}{' '}
                                          <br />
                                        </>
                                      )}
                                    </div>
                                  </>
                                )}
                            </Col>
                            <Col>
                              {item != null &&
                                item.travelAgencyAccountData !== null && (
                                  <>
                                    <span className="fw-bold small">
                                      Matkatoimisto Käyttäjä:
                                    </span>
                                    <br />
                                    <div>
                                      {item.travelAgencyAccountData
                                        .finalClientFirstName ?? ''}{' '}
                                      {item.travelAgencyAccountData
                                        .finalClientLastName ?? ''}{' '}
                                      <br />
                                      {item.travelAgencyAccountData
                                        .finalClientCompany && (
                                        <>
                                          {
                                            item.travelAgencyAccountData
                                              .finalClientCompany
                                          }{' '}
                                          <br />
                                        </>
                                      )}
                                      {
                                        item.travelAgencyAccountData
                                          .finalClientStreetAddress
                                      }{' '}
                                      <br />
                                      {
                                        item.travelAgencyAccountData
                                          .finalClientZipCode
                                      }{' '}
                                      {
                                        item.travelAgencyAccountData
                                          .finalClientCity
                                      }{' '}
                                      <br />
                                      {item.travelAgencyAccountData
                                        .travelAgencyPhone ?? ''}{' '}
                                      <br />
                                      {item.travelAgencyAccountData
                                        .travelAgencyEmail ?? ''}{' '}
                                      <br />
                                      {countries != null && (
                                        <>
                                          {countries.find(
                                            country =>
                                              country.id.toLowerCase() ===
                                              item.travelAgencyAccountData.finalClientCountry.toLowerCase(),
                                          )?.name ??
                                            t(
                                              'bookingDetails:bookingInfo.customer.noCountrySelected',
                                            )}
                                          <br />
                                        </>
                                      )}
                                    </div>
                                  </>
                                )}
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                    <hr />
                    <Heading
                      Title={t(
                        'bookingDetails:bookingInfo.reservedItems.title',
                      )}
                      Level={4}
                      CssClass="mb-24"
                    />
                    {!isTravelAgencyUser &&
                      item.accommodationReclamationText && (
                        <Alert className="mb-32">
                          <div>
                            {t(
                              'bookingDetails:bookingInfo.reservedItems.noticeTitle',
                            )}
                            :
                          </div>
                          {item.accommodationReclamationText ?? ''}
                        </Alert>
                      )}
                    <div className="table-responsive-lg">
                      <Table
                        bordered
                        className="table-data table-wrap mt-16 mb-32"
                      >
                        <thead>
                          <tr key="bookingItemsHeader">
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.propertyService',
                              )}
                            </th>
                            {!isTravelAgencyUser && (
                              <th>
                                {t(
                                  'bookingDetails:bookingInfo.orderItems.table.owner',
                                )}
                              </th>
                            )}
                            {!isTravelAgencyUser && (
                              <th>
                                {t(
                                  'bookingDetails:bookingInfo.orderItems.table.caretakers',
                                )}
                              </th>
                            )}
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.start',
                              )}
                            </th>
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.end',
                              )}
                            </th>
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.quantity',
                              )}
                            </th>
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.adults',
                              )}
                            </th>
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.children',
                              )}
                            </th>
                            <th>
                              {t(
                                'bookingDetails:bookingInfo.orderItems.table.price',
                              )}
                            </th>
                            {!isTravelAgencyUser && (
                              <>
                                <th>
                                  {t(
                                    'bookingDetails:bookingInfo.orderItems.table.purchasePrice',
                                  )}
                                </th>
                                <th>
                                  {t(
                                    'bookingDetails:bookingInfo.orderItems.table.salesCommission',
                                  )}
                                </th>
                                <th>
                                  {t(
                                    'bookingDetails:bookingInfo.orderItems.table.discount',
                                  )}
                                </th>
                                <th>
                                  {t(
                                    'bookingDetails:bookingInfo.orderItems.table.travelAgencyCommission',
                                  )}
                                </th>
                                <th>
                                  {t(
                                    'bookingDetails:bookingInfo.orderItems.table.resellerCommission',
                                  )}
                                </th>
                              </>
                            )}
                          </tr>
                        </thead>

                        <tbody>
                          {item.bookingOrderItems.map(
                            (orderItem, orderIndex) => (
                              <tr
                                key={generateItemKey(
                                  orderIndex,
                                  orderItem.id.toString(),
                                )}
                              >
                                {orderItem.isCancelled && (
                                  <td style={{ backgroundColor: '#ffcccc' }}>
                                    {orderItem.addOnNumber !== null &&
                                    orderItem.addOnNumber > 0
                                      ? orderItem.addOnNumber
                                      : ''}{' '}
                                    {orderItem.addOnNumber !== null &&
                                    orderItem.addOnNumber > 0
                                      ? orderItem.itemName
                                      : ''}{' '}
                                    {orderItem.orderItemType !== null &&
                                      orderItem.orderItemType !== undefined &&
                                      orderItem.orderItemType === 0 && (
                                        <button
                                          key={`accommodation-${orderItem.accommodationId}`}
                                          type="submit"
                                          className="booking-link bg-transparent p-0 text-start border-0"
                                          onClick={e => {
                                            e.preventDefault();
                                            window.open(
                                              `/accommodations/details/${orderItem.accommodationId}`,
                                              '_blank',
                                              `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                            );
                                          }}
                                        >
                                          {orderItem.accommodationNumber > 0
                                            ? orderItem.accommodationNumber
                                            : ''}{' '}
                                          {orderItem.itemName ?? ''}
                                        </button>
                                      )}
                                    {orderItem.includedInPrice && (
                                      <div className="text-danger">
                                        {t(
                                          'bookingDetails:bookingInfo.orderItems.includedInPrice',
                                        )}
                                      </div>
                                    )}
                                  </td>
                                )}
                                {!orderItem.isCancelled && (
                                  <td>
                                    {orderItem.addOnNumber !== null &&
                                    orderItem.addOnNumber > 0
                                      ? orderItem.addOnNumber
                                      : ''}{' '}
                                    {orderItem.addOnNumber !== null &&
                                    orderItem.addOnNumber > 0
                                      ? orderItem.itemName
                                      : ''}{' '}
                                    {!isTravelAgencyUser &&
                                      orderItem.orderItemType !== null &&
                                      orderItem.orderItemType !== undefined &&
                                      orderItem.orderItemType === 0 && (
                                        <button
                                          key={`accommodation2-${orderItem.accommodationId}`}
                                          type="submit"
                                          className="booking-link bg-transparent p-0 text-start border-0"
                                          onClick={e => {
                                            e.preventDefault();
                                            window.open(
                                              `/accommodations/details/${orderItem.accommodationId}`,
                                              '_blank',
                                              `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                            );
                                          }}
                                        >
                                          {orderItem.accommodationNumber > 0
                                            ? orderItem.accommodationNumber
                                            : ''}{' '}
                                          {orderItem.itemName ?? ''}
                                        </button>
                                      )}
                                    {isTravelAgencyUser &&
                                      orderItem.orderItemType !== null &&
                                      orderItem.orderItemType !== undefined &&
                                      orderItem.orderItemType === 0 && (
                                        <span>
                                          {orderItem.accommodationNumber > 0
                                            ? orderItem.accommodationNumber
                                            : ''}{' '}
                                          {orderItem.itemName ?? ''}
                                        </span>
                                      )}
                                    {orderItem.includedInPrice && (
                                      <div className="text-danger">
                                        {t(
                                          'bookingDetails:bookingInfo.orderItems.includedInPrice',
                                        )}
                                      </div>
                                    )}
                                  </td>
                                )}

                                {!isTravelAgencyUser && (
                                  <td>
                                    {!item.crossSiteBooking &&
                                      orderItem.ownerAccountId != null && (
                                        <button
                                          key={orderItem.ownerAccountId}
                                          type="submit"
                                          className="booking-link text-start border-0 Prl"
                                          onClick={e => {
                                            e.preventDefault();
                                            window.open(
                                              `/users/details/${orderItem.ownerAccountId}`,
                                              '_blank',
                                              `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                            );
                                          }}
                                        >
                                          {orderItem.ownerName}
                                        </button>
                                      )}
                                    {item.crossSiteBooking &&
                                      orderItem.realOwnerAccountId != null && (
                                        <button
                                          key={orderItem.realOwnerAccountId}
                                          type="submit"
                                          className="booking-link   text-start border-0 Prl"
                                          onClick={e => {
                                            e.preventDefault();
                                            window.open(
                                              `/users/details/${orderItem.realOwnerAccountId}`,
                                              '_blank',
                                              `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                            );
                                          }}
                                        >
                                          {orderItem.realOwnerName}
                                        </button>
                                      )}
                                  </td>
                                )}
                                {!isTravelAgencyUser && (
                                  <td>
                                    {orderItem.careTakers !== undefined &&
                                      orderItem.careTakers !== null &&
                                      orderItem.careTakers.map(
                                        (caretaker, indexingItem) => (
                                          <button
                                            key={`caretaker33-${caretaker.id}`}
                                            type="submit"
                                            className="booking-link bg-transparent p-0 text-start border-0 Prl"
                                            onClick={e => {
                                              e.preventDefault();
                                              window.open(
                                                `/users/details/${caretaker.id}`,
                                                '_blank',
                                                `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                              );
                                            }}
                                          >
                                            {caretaker.ownerNameNumber}
                                          </button>
                                        ),
                                      )}
                                  </td>
                                )}
                                <td>
                                  {orderItem.orderItemType !== 2
                                    ? new Date(
                                        Date.parse(orderItem.startDateTime),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : new Date(
                                        Date.parse(orderItem.arrivalDateTime),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}{' '}
                                </td>
                                <td>
                                  {orderItem.orderItemType !== 2
                                    ? new Date(
                                        Date.parse(orderItem.endDateTime),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : new Date(
                                        Date.parse(orderItem.departureDateTime),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}{' '}
                                </td>
                                <td>{orderItem.units} </td>
                                <td>{orderItem.amountOfAdults} </td>
                                <td>{orderItem.amountOfChildren} </td>
                                {!item.crossSiteBooking &&
                                  !isTravelAgencyUser && (
                                    <td>
                                      {orderItem.pricesResponse.grossPrice}
                                    </td>
                                  )}
                                {!item.crossSiteBooking &&
                                  isTravelAgencyUser && (
                                    <td>{orderItem.pricesResponse.netPrice}</td>
                                  )}
                                {item.crossSiteBooking && (
                                  <td>
                                    {
                                      orderItem.purchasePricesResponse
                                        .sitePurchasePrice
                                    }
                                  </td>
                                )}

                                {!isTravelAgencyUser &&
                                  !item.crossSiteBooking && (
                                    <td>
                                      {orderItem.purchasePricesResponse
                                        .sitePurchasePrice !== undefined &&
                                      orderItem.purchasePricesResponse
                                        .sitePurchasePrice > 0
                                        ? `${orderItem.purchasePricesResponse.sitePurchasePrice}(${orderItem.purchasePricesResponse.ownerPurchasePrice})`
                                        : orderItem.purchasePricesResponse
                                            .ownerPurchasePrice}{' '}
                                    </td>
                                  )}
                                {!isTravelAgencyUser &&
                                  item.crossSiteBooking && (
                                    <td>
                                      {orderItem.purchasePricesResponse
                                        .ownerPurchasePrice !== undefined &&
                                      orderItem.purchasePricesResponse
                                        .ownerPurchasePrice > 0
                                        ? `${orderItem.purchasePricesResponse.ownerPurchasePrice}(${orderItem.purchasePricesResponse.sitePurchasePrice})`
                                        : orderItem.purchasePricesResponse
                                            .ownerPurchasePrice}{' '}
                                    </td>
                                  )}
                                {!isTravelAgencyUser && (
                                  <>
                                    <td>
                                      {
                                        orderItem.pricesResponse
                                          .sellerCommission
                                      }{' '}
                                    </td>
                                    <td>
                                      {orderItem.pricesResponse.discount}(
                                      {orderItem.pricesResponse.discountP}
                                      %){' '}
                                    </td>
                                    <td>
                                      {
                                        orderItem.pricesResponse
                                          .travelAgencyComission
                                      }
                                      (
                                      {
                                        orderItem.pricesResponse
                                          .travelAgencyComissionP
                                      }
                                      %){' '}
                                    </td>
                                    <td>
                                      {
                                        orderItem.pricesResponse
                                          .travelAgencyOnlyComission
                                      }
                                    </td>
                                    {orderItem.optiwattiData !== null && (
                                      <td>
                                        Optiwatti status:
                                        {
                                          orderItem.optiwattiData
                                            .translatedStatus
                                        }{' '}
                                      </td>
                                    )}
                                  </>
                                )}
                              </tr>
                            ),
                          )}
                          {item.extraBookingResponses !== null &&
                            item.extraBookingResponses.length > 0 && (
                              <tr key={`extraBookingResponses${item.id}`}>
                                <td
                                  style={{
                                    border: 'none',
                                    backgroundColor: 'lightgray',
                                  }}
                                >
                                  {item.isAddOnBooking === false && (
                                    <h5 className="table-danger">
                                      {t(
                                        'bookingDetails:bookingInfo.additionalBookings.additionalBookings',
                                      )}
                                      :
                                    </h5>
                                  )}
                                  {item.isAddOnBooking === true && (
                                    <h5 className="table-danger">
                                      {t(
                                        'bookingDetails:bookingInfo.additionalBookings.mainBooking',
                                      )}
                                      :
                                    </h5>
                                  )}
                                </td>
                                <td
                                  style={{
                                    border: 'none',
                                    backgroundColor: 'lightgray',
                                  }}
                                >
                                  <div className="d-flex flex-column flex-lg-row gap-8 gap-lg-40">
                                    {item.extraBookingResponses.map(
                                      (extraBooking, extraIndex) => (
                                        <div>
                                          <button
                                            key={`extraBooking-${extraBooking.id}`}
                                            type="submit"
                                            className={`booking-link text-start border-0 ${extraBooking.isCancelled ? 'bg-danger bg-opacity-0' : ''}`}
                                            onClick={e => {
                                              e.preventDefault();
                                              window.open(
                                                `/bookings/${extraBooking.id}`,
                                                '_blank',
                                                `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                              );
                                            }}
                                          >
                                            <span>
                                              {`${extraBooking.bookingNumber} ${getBookingTitle(extraBooking)}`}
                                            </span>
                                          </button>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          {!isTravelAgencyUser && (
                            <tr>
                              <td style={{ textAlign: 'right' }} colSpan={11}>
                                {t(
                                  'bookingDetails:bookingInfo.totals.resellerTotalAmount',
                                )}
                              </td>
                              <td>{item ? item.travelAgencyNetPrice : ''}</td>
                              <td style={{ textAlign: 'right' }} colSpan={13}>
                                {t(
                                  'bookingDetails:bookingInfo.totals.bookingTotalAmount',
                                )}
                              </td>
                              <td>{item ? item.netPrice : ''}</td>
                            </tr>
                          )}
                          {isTravelAgencyUser && (
                            <tr>
                              <td style={{ textAlign: 'right' }} colSpan={13}>
                                {t(
                                  'bookingDetails:bookingInfo.totals.bookingTotalAmount',
                                )}
                              </td>
                              <td>{item ? item.travelAgencyNetPrice : ''}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                    <hr />
                    {!isTravelAgencyUser && (
                      <Heading
                        Level={4}
                        Title={t('bookingDetails:bookingInfo.invoices.title')}
                      />
                    )}
                    {!isTravelAgencyUser &&
                      (item.bookingInvoiceItems === null ||
                        item.bookingInvoiceItems.length <= 0) && (
                        <p className="mt-16 mb-32">
                          {t('bookingDetails:bookingInfo.invoices.noInvoices')}
                        </p>
                      )}
                    <Accordion defaultActiveKey="0">
                      {!isTravelAgencyUser &&
                        item.bookingInvoiceItems !== null &&
                        item.bookingInvoiceItems.filter(oi =>
                          oi.discriminator.includes('Advance'),
                        ).length > 0 && (
                          <Accordion.Item
                            key={`accordion-item-advance-single-${item.id}`}
                            eventKey="1"
                          >
                            <Accordion.Header>
                              {t(
                                'bookingDetails:bookingInfo.invoices.advanceInvoices',
                              )}
                            </Accordion.Header>
                            <Accordion.Body>
                              <Table
                                bordered
                                className="table-data table-wrap mt-16 mb-32"
                              >
                                <thead>
                                  <tr key="advanceInvoicesHeader-1">
                                    <th className="h1">
                                      {t(
                                        'bookingDetails:bookingInfo.invoices.table.invoiceNumber',
                                      )}
                                    </th>
                                    <th className="h1">
                                      {t(
                                        'bookingDetails:bookingInfo.invoices.table.status',
                                      )}
                                    </th>
                                    <th className="h1">
                                      {t(
                                        'bookingDetails:bookingInfo.invoices.table.dueDate',
                                      )}
                                    </th>
                                    <th className="h1">
                                      {t(
                                        'bookingDetails:bookingInfo.invoices.table.invoiceDate',
                                      )}
                                    </th>
                                    <th className="h1">
                                      {t(
                                        'bookingDetails:bookingInfo.invoices.table.referenceNumber',
                                      )}
                                    </th>
                                    <th className="h1">
                                      {t(
                                        'bookingDetails:bookingInfo.invoices.table.amount',
                                      )}
                                    </th>
                                    <th className="h1">Avoin saldo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {!isTravelAgencyUser &&
                                    item.bookingInvoiceItems
                                      .filter(oi =>
                                        oi.discriminator.includes('Advance'),
                                      )
                                      .map((orderItem, orderIndex) => (
                                        <tr
                                          key={generateItemKey(
                                            orderIndex,
                                            orderItem.number.toString(),
                                          )}
                                        >
                                          <td>
                                            <span className="th">
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.labels.propertyService',
                                              )}
                                            </span>
                                            <button
                                              className="booking-link text-start border-0"
                                              type="submit"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  false,
                                                  false,
                                                  'fi',
                                                  true,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceFi',
                                              )}
                                            </button>
                                            <br />
                                            <button
                                              className="booking-link text-start border-0"
                                              type="submit"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  false,
                                                  false,
                                                  'en',
                                                  true,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceEn',
                                              )}
                                            </button>
                                            <br />
                                            <button
                                              className="booking-link text-start border-0"
                                              type="submit"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  true,
                                                  false,
                                                  'fi',
                                                  true,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                              )}
                                            </button>
                                            <br />
                                            <button
                                              className="booking-link text-start border-0"
                                              type="submit"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  true,
                                                  false,
                                                  'en',
                                                  true,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                              )}
                                            </button>
                                          </td>
                                          <td>
                                            <span className="th">
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.labels.name',
                                              )}
                                            </span>
                                            {orderItem.compensationInvoiceId
                                              ? t(
                                                  'bookingDetails:bookingInfo.invoices.status.credited',
                                                )
                                              : t(
                                                  'bookingDetails:bookingInfo.invoices.status.notCredited',
                                                )}
                                          </td>
                                          <td>
                                            <span className="th">
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.labels.time',
                                              )}
                                            </span>
                                            {new Date(
                                              Date.parse(orderItem.dueDate),
                                            ).toLocaleString('fi-FI', {
                                              year: 'numeric',
                                              month: '2-digit',
                                              day: '2-digit',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                            })}{' '}
                                          </td>
                                          <td>
                                            <span className="th">
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.labels.adults',
                                              )}
                                            </span>
                                            {new Date(
                                              Date.parse(orderItem.invoiceDate),
                                            ).toLocaleString('fi-FI', {
                                              year: 'numeric',
                                              month: '2-digit',
                                              day: '2-digit',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                            })}{' '}
                                          </td>
                                          <td>
                                            <span className="th">
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.labels.children',
                                              )}
                                            </span>
                                            {orderItem.referenceNumber}{' '}
                                          </td>
                                          <td>
                                            <span className="th">
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.labels.children',
                                              )}
                                            </span>
                                            {item.advanceBillPrice
                                              ? item.advanceBillPrice
                                              : ''}{' '}
                                          </td>
                                          <td>
                                            <span className="th">
                                              Avoin saldo
                                            </span>
                                            {orderItem.openBalance &&
                                            orderItem.openBalance > 0 ? (
                                              <span style={{ color: 'red' }}>
                                                {orderItem.openBalance.toLocaleString(
                                                  'fi-FI',
                                                  {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  },
                                                )}
                                                €
                                              </span>
                                            ) : (
                                              '—'
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                </tbody>
                              </Table>
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                      {!isTravelAgencyUser &&
                        item.bookingInvoiceItems !== null &&
                        item.bookingInvoiceItems.filter(oi =>
                          oi.discriminator.includes('Final'),
                        ).length > 0 && (
                          <Accordion.Item
                            key={`accordion-item-final-single-${item.id}`}
                            eventKey="2"
                          >
                            <Accordion.Header>
                              {t(
                                'bookingDetails:bookingInfo.invoices.finalInvoices',
                              )}
                            </Accordion.Header>
                            <Accordion.Body>
                              {!isTravelAgencyUser &&
                                item.bookingInvoiceItems !== null &&
                                item.bookingInvoiceItems.filter(oi =>
                                  oi.discriminator.includes('Final'),
                                ).length > 0 && (
                                  <Table
                                    bordered
                                    className="table-data table-wrap mt-16 mb-32"
                                  >
                                    <thead>
                                      <tr key="finalInvoicesHeader-2">
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.invoiceNumber',
                                          )}
                                        </th>
                                        {/* <th className="h1"> */}
                                        {/*  {t( */}
                                        {/*    'bookingDetails:bookingInfo.invoices.table.status', */}
                                        {/*  )} */}
                                        {/* </th> */}
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.dueDate',
                                          )}
                                        </th>
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.invoiceDate',
                                          )}
                                        </th>
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.referenceNumber',
                                          )}
                                        </th>
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.amount',
                                          )}
                                        </th>
                                        <th className="h1">Avoin saldo</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {!isTravelAgencyUser &&
                                        item.bookingInvoiceItems
                                          .filter(oi =>
                                            oi.discriminator.includes('Final'),
                                          )
                                          .map((orderItem, orderIndex) => (
                                            <tr
                                              key={generateItemKey(
                                                orderIndex,
                                                orderItem.number != null
                                                  ? orderItem.number.toString()
                                                  : '',
                                              )}
                                            >
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.propertyService',
                                                  )}
                                                </span>
                                                {!item.crossSiteBooking && (
                                                  <>
                                                    <button
                                                      type="submit"
                                                      className="booking-link text-start border-0"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          false,
                                                          false,
                                                          'fi',
                                                          false,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceFi',
                                                      )}
                                                    </button>
                                                    <button
                                                      type="submit"
                                                      className="booking-link text-start border-0"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          false,
                                                          false,
                                                          'en',
                                                          false,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceEn',
                                                      )}
                                                    </button>
                                                    <br />
                                                    <button
                                                      type="submit"
                                                      className="booking-link text-start border-0"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          true,
                                                          false,
                                                          'fi',
                                                          false,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                                      )}
                                                    </button>
                                                    <br />
                                                    <button
                                                      type="submit"
                                                      className="booking-link text-start border-0"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          true,
                                                          false,
                                                          'en',
                                                          false,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                                      )}
                                                    </button>
                                                    <br />
                                                    {/* <button */}
                                                    {/*  type="submit" */}
                                                    {/*  className="booking-link text-start border-0" */}
                                                    {/*  onClick={e => { */}
                                                    {/*    e.preventDefault(); */}
                                                    {/*    window.open( */}
                                                    {/*      `/bookings/en/${item.id}/invoice/${orderItem.number}/true`, */}
                                                    {/*      '_blank', */}
                                                    {/*      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`, */}
                                                    {/*    ); */}
                                                    {/*  }} */}
                                                    {/* > */}
                                                    {/*  {orderItem.number ?? ''} avaa lasku */}
                                                    {/*  en(kopio) */}
                                                    {/* </button> */}
                                                    <button
                                                      type="submit"
                                                      className="booking-link text-start border-0"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          true,
                                                          false,
                                                          'en',
                                                          false,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                                      )}
                                                    </button>
                                                  </>
                                                )}
                                                {item.crossSiteBooking && (
                                                  <button
                                                    type="submit"
                                                    className="booking-link  text-start border-0"
                                                    onClick={() =>
                                                      getPdfInvoice(
                                                        orderItem.id,
                                                        true,
                                                        false,
                                                        'fi',
                                                        false,
                                                      )
                                                    }
                                                  >
                                                    {orderItem.number ?? ''}{' '}
                                                    {t(
                                                      'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                                    )}
                                                  </button>
                                                )}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.name',
                                                  )}
                                                </span>
                                                {orderItem.compensationInvoiceId
                                                  ? t(
                                                      'bookingDetails:bookingInfo.invoices.status.credited',
                                                    )
                                                  : t(
                                                      'bookingDetails:bookingInfo.invoices.status.notCredited',
                                                    )}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.time',
                                                  )}
                                                </span>
                                                {new Date(
                                                  Date.parse(orderItem.dueDate),
                                                ).toLocaleString('fi-FI', {
                                                  year: 'numeric',
                                                  month: '2-digit',
                                                  day: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                })}{' '}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.adults',
                                                  )}
                                                </span>
                                                {new Date(
                                                  Date.parse(
                                                    orderItem.invoiceDate,
                                                  ),
                                                ).toLocaleString('fi-FI', {
                                                  year: 'numeric',
                                                  month: '2-digit',
                                                  day: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                })}{' '}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.children',
                                                  )}
                                                </span>
                                                {orderItem.referenceNumber ??
                                                  ''}{' '}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.children',
                                                  )}
                                                </span>
                                                {item.finalBillPrice
                                                  ? item.finalBillPrice
                                                  : ''}{' '}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  Avoin saldo
                                                </span>
                                                {orderItem.openBalance &&
                                                orderItem.openBalance > 0 ? (
                                                  <span
                                                    style={{ color: 'red' }}
                                                  >
                                                    {orderItem.openBalance.toLocaleString(
                                                      'fi-FI',
                                                      {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                      },
                                                    )}
                                                    €
                                                  </span>
                                                ) : (
                                                  '—'
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                    </tbody>
                                  </Table>
                                )}
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                      {!isTravelAgencyUser &&
                        item.bookingInvoiceItems !== null &&
                        item.bookingInvoiceItems.filter(oi =>
                          oi.discriminator.includes('Compensation'),
                        ).length > 0 && (
                          <Accordion.Item
                            eventKey="3"
                            key={`accordion-item-compensation-single-${item.id}`}
                          >
                            <Accordion.Header>
                              {t(
                                'bookingDetails:bookingInfo.invoices.creditInvoices',
                              )}
                            </Accordion.Header>
                            <Accordion.Body>
                              {!isTravelAgencyUser &&
                                item.bookingInvoiceItems !== null &&
                                item.bookingInvoiceItems.filter(oi =>
                                  oi.discriminator.includes('Compensation'),
                                ).length > 0 && (
                                  <Table
                                    bordered
                                    className="table-data table-wrap mt-16 mb-32"
                                  >
                                    <thead>
                                      <tr key="compensationInvoicesHeader-3">
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.invoiceNumber',
                                          )}
                                        </th>
                                        {/* <th className="h1"> */}
                                        {/*  {t( */}
                                        {/*    'bookingDetails:bookingInfo.invoices.table.status', */}
                                        {/*  )} */}
                                        {/* </th> */}
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.dueDate',
                                          )}
                                        </th>
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.invoiceDate',
                                          )}
                                        </th>
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.referenceNumber',
                                          )}
                                        </th>
                                        <th className="h1">
                                          {t(
                                            'bookingDetails:bookingInfo.invoices.table.amount',
                                          )}
                                        </th>
                                        <th className="h1">Avoin saldo</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {!isTravelAgencyUser &&
                                        item.bookingInvoiceItems
                                          .filter(oi =>
                                            oi.discriminator.includes(
                                              'Compensation',
                                            ),
                                          )
                                          .map((orderItem, orderIndex) => (
                                            <tr
                                              key={generateItemKey(
                                                orderIndex,
                                                orderItem.number != null
                                                  ? orderItem.number.toString()
                                                  : '',
                                              )}
                                            >
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.propertyService',
                                                  )}
                                                </span>
                                                {!item.crossSiteBooking && (
                                                  <>
                                                    <button
                                                      className="booking-link  text-start border-0"
                                                      type="submit"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          false,
                                                          true,
                                                          'fi',
                                                          orderItem.isAdvance,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceFi',
                                                      )}{' '}
                                                      {orderItem.isAdvance
                                                        ? t(
                                                            'bookingDetails:bookingInfo.invoices.types.advance',
                                                          )
                                                        : t(
                                                            'bookingDetails:bookingInfo.invoices.types.final',
                                                          )}
                                                    </button>
                                                    <br />
                                                    <button
                                                      className="booking-link  text-start border-0"
                                                      type="submit"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          false,
                                                          true,
                                                          'en',
                                                          orderItem.isAdvance,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceEn',
                                                      )}{' '}
                                                      {orderItem.isAdvance
                                                        ? t(
                                                            'bookingDetails:bookingInfo.invoices.types.advance',
                                                          )
                                                        : t(
                                                            'bookingDetails:bookingInfo.invoices.types.final',
                                                          )}
                                                    </button>
                                                    <br />
                                                    <button
                                                      className="booking-link text-start border-0 border-0"
                                                      type="submit"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          true,
                                                          true,
                                                          'fi',
                                                          orderItem.isAdvance,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                                      )}
                                                      {orderItem.isAdvance
                                                        ? t(
                                                            'bookingDetails:bookingInfo.invoices.types.advance',
                                                          )
                                                        : t(
                                                            'bookingDetails:bookingInfo.invoices.types.final',
                                                          )}
                                                    </button>
                                                    <br />
                                                    <button
                                                      className="booking-link text-start border-0 border-0"
                                                      type="submit"
                                                      onClick={() =>
                                                        getPdfInvoice(
                                                          orderItem.id,
                                                          true,
                                                          true,
                                                          'en',
                                                          orderItem.isAdvance,
                                                        )
                                                      }
                                                    >
                                                      {orderItem.number ?? ''}{' '}
                                                      {t(
                                                        'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                                      )}
                                                      {orderItem.isAdvance
                                                        ? t(
                                                            'bookingDetails:bookingInfo.invoices.types.advance',
                                                          )
                                                        : t(
                                                            'bookingDetails:bookingInfo.invoices.types.final',
                                                          )}
                                                    </button>
                                                  </>
                                                )}
                                                {item.crossSiteBooking && (
                                                  <button
                                                    className="booking-link text-start border-0 border-0"
                                                    type="submit"
                                                    onClick={() =>
                                                      getPdfInvoice(
                                                        orderItem.id,
                                                        true,
                                                        true,
                                                        'fi',
                                                        orderItem.isAdvance,
                                                      )
                                                    }
                                                  >
                                                    {orderItem.number ?? ''}{' '}
                                                    {t(
                                                      'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                                    )}
                                                    {orderItem.isAdvance
                                                      ? t(
                                                          'bookingDetails:bookingInfo.invoices.types.advance',
                                                        )
                                                      : t(
                                                          'bookingDetails:bookingInfo.invoices.types.final',
                                                        )}
                                                  </button>
                                                )}
                                                <br />
                                                {item.crossSiteBooking && (
                                                  <button
                                                    className="booking-link text-start border-0 border-0"
                                                    type="submit"
                                                    onClick={() =>
                                                      getPdfInvoice(
                                                        orderItem.id,
                                                        true,
                                                        true,
                                                        'en',
                                                        orderItem.isAdvance,
                                                      )
                                                    }
                                                  >
                                                    {orderItem.number ?? ''}{' '}
                                                    {t(
                                                      'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                                    )}
                                                    {orderItem.isAdvance
                                                      ? t(
                                                          'bookingDetails:bookingInfo.invoices.types.advance',
                                                        )
                                                      : t(
                                                          'bookingDetails:bookingInfo.invoices.types.final',
                                                        )}
                                                  </button>
                                                )}
                                              </td>
                                              {/* <td> */}
                                              {/*  <span className="th"> */}
                                              {/*    {t( */}
                                              {/*      'bookingDetails:bookingInfo.invoices.labels.name', */}
                                              {/*    )} */}
                                              {/*  </span> */}
                                              {/*  {orderItem.compensationInvoiceId */}
                                              {/*    ? t( */}
                                              {/*        'bookingDetails:bookingInfo.invoices.status.credited', */}
                                              {/*      ) */}
                                              {/*    : t( */}
                                              {/*        'bookingDetails:bookingInfo.invoices.status.notCredited', */}
                                              {/*      )} */}
                                              {/* </td> */}
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.time',
                                                  )}
                                                </span>
                                                {new Date(
                                                  Date.parse(orderItem.dueDate),
                                                ).toLocaleString('fi-FI', {
                                                  year: 'numeric',
                                                  month: '2-digit',
                                                  day: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                })}{' '}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.adults',
                                                  )}
                                                </span>
                                                {new Date(
                                                  Date.parse(
                                                    orderItem.invoiceDate,
                                                  ),
                                                ).toLocaleString('fi-FI', {
                                                  year: 'numeric',
                                                  month: '2-digit',
                                                  day: '2-digit',
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                })}{' '}
                                              </td>
                                              <td>
                                                <span className="th">
                                                  {t(
                                                    'bookingDetails:bookingInfo.invoices.labels.children',
                                                  )}
                                                </span>
                                                {orderItem.referenceNumber ??
                                                  ''}{' '}
                                              </td>
                                              {!item.crossSiteBooking && (
                                                <td>
                                                  {orderItem.isAdvance &&
                                                  item.advanceBillPrice
                                                    ? -item.advanceBillPrice
                                                    : ''}{' '}
                                                  {!orderItem.isAdvance &&
                                                  item.finalBillPrice
                                                    ? -item.finalBillPrice
                                                    : ''}{' '}
                                                </td>
                                              )}
                                              {item.crossSiteBooking && (
                                                <td>
                                                  {item.invoiceVisiblePrice
                                                    ? -item.invoiceVisiblePrice
                                                    : ''}{' '}
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                    </tbody>
                                  </Table>
                                )}
                            </Accordion.Body>
                          </Accordion.Item>
                        )}
                    </Accordion>
                    <hr />
                    <Heading
                      Level={4}
                      Title={t(
                        'bookingDetails:bookingInfo.communication.title',
                      )}
                    />
                    <Table bordered className="table-data mt-16">
                      <thead>
                        <tr key="customerBookingNotesHeader2">
                          <th className="ps-24">
                            {t(
                              'bookingDetails:bookingInfo.communication.notes.customer',
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {!editCustomerNote &&
                          item.customerBookingNotes.map(
                            (orderItem, orderIndex) => (
                              <tr
                                key={`print1Note${generateItemKey(
                                  orderIndex,
                                  orderItem.noteText.toString(),
                                )}`}
                              >
                                <td>{orderItem.noteText}</td>
                              </tr>
                            ),
                          )}
                      </tbody>
                    </Table>
                    {!isTravelAgencyUser && (
                      <>
                        <Table bordered className="table-data mt-16">
                          <thead>
                            <tr key="ownerBookingNotesHeader2">
                              <th className="ps-24">
                                {t(
                                  'bookingDetails:bookingInfo.communication.notes.owner',
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.ownerBookingNotes.map(
                              (orderItem, orderIndex) => (
                                <tr
                                  style={{ whiteSpace: 'pre-wrap' }}
                                  key={generateItemKey(
                                    orderIndex,
                                    `${orderItem.noteText.toString()}1`,
                                  )}
                                >
                                  <td>{orderItem.noteText}</td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                        <Table bordered className="table-data mt-16">
                          <thead>
                            <tr key="maintainerBookingNotesHeader2">
                              <th className="ps-24">
                                {t(
                                  'bookingDetails:bookingInfo.communication.notes.clerk',
                                )}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.officerBookingNotes.map(
                              (orderItem, orderIndex) => (
                                <tr
                                  key={generateItemKey(
                                    orderIndex,
                                    `${orderItem.noteText.toString()}2`,
                                  )}
                                >
                                  <td>{orderItem.noteText}</td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </Table>
                        {/* Suomulla tarviiko vain yhden viestin asiakas, ei vaiheittaisia? */}
                        {/*
                <div className="row my-48 gx-48">
                  <div className="col">
                    <h3 className="h4 mb-16">
                      Lähetä varausvahvistus asiakkaalle
                    </h3>
                    <Form.Label htmlFor="clientEmail" className="required">
                      Sähköpostiosoite
                    </Form.Label>
                    <InputGroup className="flex-nowrap">
                      <InputText
                        Id="clientEmail"
                        Name="clientEmail"
                        IsRequired
                        HandleChange={event =>
                          setClientBookingConfirmationToSentEmailAddress(
                            event.target.value,
                          )
                        }
                        Value={clientBookingConfirmationToSentEmailAddress}
                        ClassName="mb-0 flex-fill rounded-end-0"
                      />
                      <Button
                        onClick={() => sendClientBookingConfirmation()}
                        disabled={
                          !isValidEmail(
                            clientBookingConfirmationToSentEmailAddress,
                          )
                        }
                        className="ms-n8 px-16"
                      >
                        Lähetä
                      </Button>
                    </InputGroup>
                  </div>
                  <div className="col">
                    <BookingOwnersEmailComponent
                      bookingOwners={bookingOwners} // Pass bookingOwners to the child
                      sendOwnerBookingConfirmation={
                        sendOwnerBookingConfirmation
                      } // Pass the send function to the child
                    />
                  </div>
                  <div className="col">
                    <BookingMaintainersEmailComponent
                      bookingMaintainers={bookingMaintainers}
                      sendMaintainerBookingConfirmation={
                        sendMaintainerBookingConfirmation
                      }
                    />
                  </div>
                </div>
                */}

                        {showPhase123EmailBoxes(item) && (
                          <>
                            <div className="row my-48 gx-48">
                              <div className="col-md-4">
                                <h3 className="h4 mb-16">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.phase1.title',
                                  )}
                                </h3>
                                <Form.Label
                                  htmlFor="clientEmailPhase1"
                                  className="required"
                                >
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.phase1.emailLabel',
                                  )}
                                </Form.Label>
                                <InputGroup className="flex-nowrap">
                                  <InputText
                                    Id="clientEmailPhase1"
                                    Name="clientEmailPhase1"
                                    IsRequired
                                    HandleChange={event =>
                                      setClientBookingPhase1ToSentEmailAddress(
                                        event.target.value,
                                      )
                                    }
                                    Value={
                                      clientBookingPhase1ToSentEmailAddress
                                    }
                                    ClassName="mb-0 flex-fill rounded-end-0"
                                  />
                                  <Button
                                    onClick={() =>
                                      sendClientAnyPhaseConfirmation(
                                        ClientMessagePhase.Phase1,
                                        item.id,
                                      )
                                    }
                                    disabled={
                                      !isValidEmails(
                                        clientBookingPhase1ToSentEmailAddress,
                                      )
                                    }
                                    className="ms-n8 px-16"
                                  >
                                    {t(
                                      'bookingDetails:bookingInfo.messaging.send',
                                    )}
                                  </Button>
                                </InputGroup>
                              </div>
                              <div className="col-md-4">
                                <h3 className="h4 mb-16">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.phase2.title',
                                  )}
                                </h3>
                                <Form.Label
                                  htmlFor="clientEmailPhase2"
                                  className="required"
                                >
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.phase2.emailLabel',
                                  )}
                                </Form.Label>
                                <InputGroup className="flex-nowrap">
                                  <InputText
                                    Id="clientEmailPhase2"
                                    Name="clientEmailPhase2"
                                    IsRequired
                                    HandleChange={event =>
                                      setClientBookingPhase2ToSentEmailAddress(
                                        event.target.value,
                                      )
                                    }
                                    Value={
                                      clientBookingPhase2ToSentEmailAddress
                                    }
                                    ClassName="mb-0 flex-fill rounded-end-0"
                                  />
                                  <Button
                                    onClick={() =>
                                      sendClientAnyPhaseConfirmation(
                                        ClientMessagePhase.Phase2,
                                        item.id,
                                      )
                                    }
                                    disabled={
                                      !isValidEmails(
                                        clientBookingPhase2ToSentEmailAddress,
                                      )
                                    }
                                    className="ms-n8 px-16"
                                  >
                                    {t(
                                      'bookingDetails:bookingInfo.messaging.send',
                                    )}
                                  </Button>
                                </InputGroup>
                              </div>
                              <div className="col-md-4">
                                <h3 className="h4 mb-16">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.phase3.title',
                                  )}
                                </h3>
                                <Form.Label
                                  htmlFor="clientEmailPhase3"
                                  className="required"
                                >
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.phase3.emailLabel',
                                  )}
                                </Form.Label>
                                <InputGroup className="flex-nowrap">
                                  <InputText
                                    Id="clientEmailPhase3"
                                    Name="clientEmailPhase3"
                                    IsRequired
                                    HandleChange={event =>
                                      setClientBookingPhase3ToSentEmailAddress(
                                        event.target.value,
                                      )
                                    }
                                    Value={
                                      clientBookingPhase3ToSentEmailAddress
                                    }
                                    ClassName="mb-0 flex-fill rounded-end-0"
                                  />
                                  <Button
                                    onClick={() =>
                                      sendClientAnyPhaseConfirmation(
                                        ClientMessagePhase.Phase3,
                                        item.id,
                                      )
                                    }
                                    disabled={
                                      !isValidEmails(
                                        clientBookingPhase3ToSentEmailAddress,
                                      )
                                    }
                                    className="ms-n8 px-16"
                                  >
                                    {t(
                                      'bookingDetails:bookingInfo.messaging.send',
                                    )}
                                  </Button>
                                </InputGroup>
                              </div>
                            </div>
                            <div className="row my-48 gx-48">
                              <div className="col-md-4 offset-md-4">
                                <h3 className="h4 mb-16">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.invoices.title',
                                  )}
                                </h3>
                                <Form.Label
                                  htmlFor="clientEmailBills"
                                  className="required"
                                >
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.invoices.emailLabel',
                                  )}
                                </Form.Label>
                                <InputGroup className="flex-nowrap">
                                  <InputText
                                    Id="clientEmailBills"
                                    Name="clientEmailBills"
                                    IsRequired
                                    HandleChange={event =>
                                      setClientBookingBillsToSentEmailAddress(
                                        event.target.value,
                                      )
                                    }
                                    Value={clientBookingBillsToSentEmailAddress}
                                    ClassName="mb-0 flex-fill rounded-end-0"
                                  />
                                  <Button
                                    onClick={() =>
                                      sendClientBillsFromBooking(item.id)
                                    }
                                    disabled={
                                      !isValidEmail(
                                        clientBookingBillsToSentEmailAddress,
                                      )
                                    }
                                    className="ms-n8 px-16"
                                  >
                                    {t(
                                      'bookingDetails:bookingInfo.messaging.send',
                                    )}
                                  </Button>
                                </InputGroup>
                              </div>
                            </div>
                            <div className="row my-48 gx-48">
                              <div className="col">
                                <BookingOwnersEmailComponent
                                  bookingId={item.id}
                                  bookingOwners={
                                    getOwnersMaintainersForBooking(item)
                                      .bookingOwners
                                  }
                                  sendOwnerBookingConfirmation={
                                    sendOwnerBookingConfirmation
                                  }
                                />
                              </div>
                              <div className="col">
                                <BookingMaintainersEmailComponent
                                  bookingId={item.id}
                                  bookingMaintainersAsRoles={
                                    getOwnersMaintainersForBooking(item)
                                      .bookingMaintainersAsRoles
                                  }
                                  bookingMaintainers={
                                    getOwnersMaintainersForBooking(item)
                                      .bookingMaintainers
                                  }
                                  sendMaintainerBookingConfirmation={
                                    sendMaintainerBookingConfirmation
                                  }
                                />
                              </div>
                            </div>
                          </>
                        )}
                        {showEmailConfirmationSuomu(item) && (
                          <div className="col-md-4">
                            <h3 className="h4 mb-16">
                              Lähetä varausvahvistus asiakkaalle
                            </h3>
                            <Form.Label
                              htmlFor="clientEmailPhase1"
                              className="required"
                            >
                              {t(
                                'bookingDetails:bookingInfo.messaging.phase1.emailLabel',
                              )}
                            </Form.Label>
                            <InputGroup className="flex-nowrap">
                              <InputText
                                Id="clientEmailPhase1"
                                Name="clientEmailPhase1"
                                IsRequired
                                HandleChange={event =>
                                  setClientBookingPhase1ToSentEmailAddress(
                                    event.target.value,
                                  )
                                }
                                Value={clientBookingPhase1ToSentEmailAddress}
                                ClassName="mb-0 flex-fill rounded-end-0"
                              />
                              <Button
                                onClick={() =>
                                  sendClientAnyPhaseConfirmation(
                                    ClientMessagePhase.Phase1,
                                    item.id,
                                  )
                                }
                                disabled={
                                  !isValidEmails(
                                    clientBookingPhase1ToSentEmailAddress,
                                  )
                                }
                                className="ms-n8 px-16"
                              >
                                {t('bookingDetails:bookingInfo.messaging.send')}
                              </Button>
                            </InputGroup>
                          </div>
                        )}
                        {showClientCancellationEmailBox(item) && (
                          <div className="row my-48 gx-48">
                            <div className="col-md-4">
                              <h3 className="h4 mb-16">
                                {t(
                                  'bookingDetails:bookingInfo.messaging.cancellation.title',
                                )}
                              </h3>
                              <Form.Label
                                htmlFor="clientEmailCancellation"
                                className="required"
                              >
                                {t(
                                  'bookingDetails:bookingInfo.messaging.cancellation.emailLabel',
                                )}
                              </Form.Label>
                              <InputGroup className="flex-nowrap">
                                <InputText
                                  Id="clientEmailCancellation"
                                  Name="clientEmailCancellation"
                                  IsRequired
                                  HandleChange={event =>
                                    setClientBookingCancellationToSentEmailAddress(
                                      event.target.value,
                                    )
                                  }
                                  Value={
                                    clientBookingCancellationToSentEmailAddress
                                  }
                                  ClassName="mb-0 flex-fill rounded-end-0"
                                />
                                <Button
                                  onClick={() =>
                                    sendClientCancellationConfirmation(item.id)
                                  }
                                  disabled={
                                    !isValidEmails(
                                      clientBookingCancellationToSentEmailAddress,
                                    )
                                  }
                                  className="ms-n8 px-16"
                                >
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.send',
                                  )}
                                </Button>
                              </InputGroup>
                            </div>
                          </div>
                        )}
                        <div className="row my-48 gx-48">
                          <div className="col">
                            <Button
                              onClick={() => refreshBookingMessageEvents()}
                              className="ms-n8 px-16"
                            >
                              {t(
                                'bookingDetails:bookingInfo.messaging.messageEvents.refreshButton',
                              )}
                            </Button>
                          </div>
                          <div className="col">
                            <Loader
                              IsLoading={
                                BookingMessageEventsStatus ===
                                LOADING_STATUS.LOADING
                              }
                            />
                          </div>
                        </div>
                        {bookingMessageEventsContainer != null && (
                          <Table bordered className="table-data mt-16">
                            <thead>
                              <tr key="bookingMessageEventsHeader2">
                                <th className="ps-24">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.events.table.title',
                                  )}
                                </th>
                                <th className="ps-24">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.events.table.messageType',
                                  )}
                                </th>
                                <th className="ps-24">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.events.table.emailAddress',
                                  )}
                                </th>
                                <th className="ps-24">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.events.table.phoneNumber',
                                  )}
                                </th>
                                <th className="ps-24">
                                  {t(
                                    'bookingDetails:bookingInfo.messaging.events.table.actions',
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {bookingMessageEventsContainer
                                .filter(
                                  be =>
                                    be.bookingId.toLowerCase() ===
                                    item.id.toLowerCase(),
                                )
                                .slice()
                                .sort(
                                  (a, b) =>
                                    new Date(b.sentTime).getTime() -
                                    new Date(a.sentTime).getTime(),
                                ) // Sorting by sentTime
                                .map((messageEvent, orderIndex) => (
                                  <tr
                                    key={generateItemKey(
                                      orderIndex,
                                      messageEvent.id,
                                    )}
                                  >
                                    <td>
                                      {new Date(
                                        Date.parse(messageEvent.sentTime),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}{' '}
                                    </td>
                                    <td>
                                      {messageEventTypeTranslations[
                                        currentLanguage
                                      ][messageEvent.bookingMessageEventType] ||
                                        t(
                                          'bookingDetails:bookingInfo.messaging.events.table.unknown',
                                        )}
                                    </td>
                                    <td>{messageEvent.emailAddresses}</td>
                                    <td>{messageEvent.phoneNumbers}</td>
                                    <td>
                                      {messageEvent.transactionalMessageDtoJson?.trim() && (
                                        <button
                                          type="button"
                                          className="text-blue-600 underline hover:text-blue-800 border-0 bg-transparent p-0"
                                          onClick={e => {
                                            e.preventDefault();
                                            const popup = window.open(
                                              '',
                                              '_blank',
                                              `width=1000,height=700,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 350}`,
                                            );
                                            if (
                                              popup &&
                                              messageEvent.transactionalMessageDtoJson
                                            ) {
                                              const prettyJson = JSON.stringify(
                                                JSON.parse(
                                                  messageEvent.transactionalMessageDtoJson,
                                                ),
                                                null,
                                                2,
                                              );
                                              popup.document.write(`
                                              <html>
                                                <head>
                                                  <title>${t('bookingDetails:bookingInfo.messaging.events.jsonPopup.title')}</title>
                                                  <style>
                                                    body { font-family: monospace; padding: 1rem; background: #f9f9f9; color: #333; }
                                                    pre { white-space: pre-wrap; word-wrap: break-word; }
                                                  </style>
                                                </head>
                                                <body>
                                                  <h2>${t('bookingDetails:bookingInfo.messaging.events.jsonPopup.title')}</h2>
                                                  <pre>${prettyJson}</pre>
                                                </body>
                                              </html>
                                            `);
                                              popup.document.close();
                                            }
                                          }}
                                        >
                                          {t(
                                            'bookingDetails:bookingInfo.messaging.events.table.viewJson',
                                          )}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        )}
                      </>
                    )}
                  </React.Fragment>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ))
      : (bookingContainer?.bookingResponses || [])
          .filter(Boolean)
          .map((item, index) => (
            <React.Fragment key={generateItemKey(index, item.id.toString())}>
              <div className="d-flex flex-column flex-lg-row gap-16 justify-content-end mb-32">
                {!isTravelAgencyUser &&
                  item.isCancelled === false &&
                  item.isAllowedToEdit &&
                  item.bookingType !== 1 &&
                  item.bookingType !== 2 &&
                  item.bookingType !== 4 &&
                  item.bookingType !== 6 &&
                  item.bookingType !== 8 &&
                  item.bookingType !== 10 && (
                    <Button
                      onClick={() => editBooking(index)}
                      variant="outline-blue"
                      disabled={item.isCancelled}
                    >
                      {t('common:buttons.modify')}
                    </Button>
                  )}
                {!isTravelAgencyUser &&
                  item.isCancelled === false &&
                  item.isAllowedToEdit &&
                  item.bookingOrderItems.find(oi => oi.orderItemType === 0) &&
                  (item.bookingType === 1 ||
                    item.bookingType === 2 ||
                    item.bookingType === 4 ||
                    item.bookingType === 6 ||
                    item.bookingType === 8 ||
                    item.bookingType === 10) &&
                  !getAllowPastAdditional(item) && (
                    <Button
                      onClick={
                        item.isCancelled
                          ? null
                          : () => showAdditionalModal(item)
                      }
                      disabled={
                        !item.isAllowedToEdit ||
                        !item.isAllowedToCancel ||
                        item.isCancelled
                      }
                      variant="outline-blue"
                      className="text-nowrap"
                    >
                      {t(
                        'bookingDetails:bookingInfo.buttons.makeAdditionalBooking',
                      )}
                    </Button>
                  )}
                {!isTravelAgencyUser &&
                  item.isCancelled === false &&
                  item.isAllowedToEdit &&
                  item.bookingOrderItems.find(oi => oi.orderItemType === 0) &&
                  (item.bookingType === 1 ||
                    item.bookingType === 2 ||
                    item.bookingType === 4 ||
                    item.bookingType === 6 ||
                    item.bookingType === 8 ||
                    item.bookingType === 10) &&
                  getAllowPastAdditional(item) && (
                    <Button
                      onClick={
                        item.isCancelled
                          ? null
                          : () => showAdditionalModal(item)
                      }
                      disabled={!getAllowPastAdditional(item)}
                      variant="outline-blue"
                      className="text-nowrap"
                    >
                      {t(
                        'bookingDetails:bookingInfo.buttons.makeAdditionalBookingToPast',
                      )}
                    </Button>
                  )}
                {!isTravelAgencyUser &&
                  item.isCancelled === false &&
                  item.isAllowedToCancel &&
                  item.isAllowedToEdit &&
                  (item.bookingType === 0 ||
                    item.bookingType === 1 ||
                    item.bookingType === 2 ||
                    item.bookingType === 4 ||
                    item.bookingType === 5 ||
                    item.bookingType === 13 ||
                    item.bookingType === 6 ||
                    item.bookingType === 8 ||
                    item.bookingType === 3 ||
                    item.bookingType === 9 ||
                    item.bookingType === 10) && (
                    <Button
                      onClick={
                        item.isCancelled ? null : () => showCancelModal(item)
                      }
                      disabled={
                        !item.isAllowedToEdit ||
                        !item.isAllowedToCancel ||
                        item.isCancelled
                      }
                      variant="danger"
                      className="text-nowrap"
                    >
                      {t('bookingDetails:bookingInfo.buttons.cancelBooking')}
                    </Button>
                  )}
              </div>
              <BasicBookingData
                item={item}
                labels={{
                  bookingNumber: t(
                    'bookingDetails:bookingInfo.labels.bookingNumber',
                  ),
                  keyCode: t('bookingDetails:bookingInfo.labels.keyCode'),
                  bookingType: t(
                    'bookingDetails:bookingInfo.labels.bookingType',
                  ),
                  created: t('bookingDetails:bookingInfo.labels.created'),
                  modifiedConfirmed: t(
                    'bookingDetails:bookingInfo.labels.modifiedConfirmed',
                  ),
                  validUntil: t('bookingDetails:bookingInfo.labels.validUntil'),
                  clerk: t('bookingDetails:bookingInfo.labels.clerk'),
                  travelAgency: t(
                    'bookingDetails:bookingInfo.externalBooking.travelAgency',
                  ),
                  interhome: t('bookingDetails:bookingInfo.labels.interhome'),
                  interhomeYllaksenmatkailu: t(
                    'bookingDetails:bookingInfo.labels.interhomeYllaksenmatkailu',
                  ),
                  agent: t('bookingDetails:bookingInfo.labels.agent'),
                }}
                bookingTypeText={getBookingTypeTranslation(
                  item.bookingType,
                  currentLanguage,
                )}
                isTravelAgencyUser={isTravelAgencyUser}
              />
              {item.isCancelled && (
                <Alert variant="danger" className="mb-32">
                  {t('bookingDetails:bookingInfo.bookingIsCancelled')}
                  <div>
                    {t('bookingDetails:bookingInfo.cancelledAt')}:{' '}
                    {new Date(
                      Date.parse(item.cancelledAt ?? ''),
                    ).toLocaleString('fi-FI', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    <div>
                      Peruja:
                      {item.cancellerFirstName ?? ''}
                      {item.cancellerLastName ?? ''}
                    </div>
                  </div>
                </Alert>
              )}
              {!item.isCancelled &&
                !item.isAddOnBooking &&
                !(
                  item.externalBookingIdsDto.externalBookingSource ===
                    'TRAVEL_AGENCY' ||
                  item.externalBookingIdsDto.externalBookingSource ===
                    'INTERHOME' ||
                  item.externalBookingIdsDto.externalBookingSource ===
                    'INTERHOME_YLLAKSENMATKAILU'
                ) &&
                (item.bookingType === 3 ||
                  item.bookingType === 4 ||
                  item.bookingType === 9 ||
                  item.bookingType === 10) &&
                item.paymentMethod === null && (
                  <Alert
                    variant="danger"
                    className="mb-32"
                    style={{
                      backgroundColor: '#ff6b6b',
                      fontSize: '16px',
                      fontWeight: '500',
                    }}
                  >
                    {t('bookingDetails:bookingInfo.incompleteBookingWarning')}
                  </Alert>
                )}
              {(item.bookingType === 3 ||
                item.bookingType === 4 ||
                item.bookingType === 9 ||
                item.bookingType === 10) &&
                !(
                  item.externalBookingIdsDto &&
                  item.externalBookingIdsDto.externalBookingSource &&
                  (item.externalBookingIdsDto.externalBookingSource ===
                    'TRAVEL_AGENCY' ||
                    item.externalBookingIdsDto.externalBookingSource ===
                      'INTERHOME' ||
                    item.externalBookingIdsDto.externalBookingSource ===
                      'INTERHOME_YLLAKSENMATKAILU')
                ) &&
                item.paymentMethod !== null && (
                  <Alert className="mb-32">
                    <div>{t('bookingDetails:bookingInfo.payment.title')}:</div>
                    {item.onlinePaymentCompleted && (
                      <>
                        <div className="mb-32">
                          {t(
                            'bookingDetails:bookingInfo.payment.onlinePaymentSuccessful',
                          )}
                        </div>
                        {item &&
                          item.advanceBilled &&
                          item.paymentAmount !== null &&
                          item.paymentAmount > 0 && (
                            <div>
                              {t('bookingDetails:bookingInfo.advancePaid')}
                            </div>
                          )}
                        {item &&
                          (item.advanceBilled === null ||
                            item.advanceBilled === undefined ||
                            item.advanceBilled === false) &&
                          item.paymentAmount !== null &&
                          item.paymentAmount > 0 && (
                            <div>
                              {t(
                                'bookingDetails:bookingInfo.payment.fullyPaid',
                              )}
                            </div>
                          )}
                        <span>
                          {t('bookingDetails:bookingInfo.payment.paidAmount', {
                            amount: item.paymentAmount,
                          })}
                        </span>
                      </>
                    )}
                    {!item.onlinePaymentCompleted &&
                      item.paymentMethod !== null && (
                        <Alert variant="danger" className="mb-32">
                          {t(
                            'bookingDetails:bookingInfo.payment.onlinePaymentFailed',
                          )}
                        </Alert>
                      )}
                    {item.paymentMethod && (
                      <div className="mb-32">{item.paymentMethod}</div>
                    )}
                  </Alert>
                )}
              {item.reasonOfCancellation &&
                item.reasonOfCancellation !== '' && (
                  <Alert variant="primary" className="mb-32">
                    {t('bookingDetails:bookingInfo.cancellationReason', {
                      reason: item.reasonOfCancellation,
                    })}
                  </Alert>
                )}

              <hr />

              <Heading
                Level={4}
                Title={t('bookingDetails:bookingInfo.customer.title')}
              />
              <div className="d-flex flex-column flex-lg-row gap-24 justify-content-start my-16 mb-32">
                <div className="d-flex flex-column flex-lg-row gap-8 gap-lg-40">
                  <div>
                    <Row>
                      <Col>
                        <span className="fw-bold small">
                          {t('bookingDetails:bookingInfo.customer.subTitle')}
                        </span>
                        {!isTravelAgencyUser &&
                          item.payerId !== undefined &&
                          item.payerId !== null &&
                          item.canViewCustomer && (
                            <button
                              type="submit"
                              className="btn btn-link booking-link bg-transparent p-0 text-start border-0"
                              onClick={e => {
                                e.preventDefault();
                                window.open(
                                  `/customers/details/${item.payerId}`,
                                  '_blank',
                                  `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                );
                              }}
                            >
                              {item.payerAccountNumber ?? ''}{' '}
                              {item.payerFirstName ?? ''}{' '}
                              {item.payerLastName ?? ''}{' '}
                            </button>
                          )}
                        <br />
                        {isTravelAgencyUser ||
                          (!item.canViewCustomer && (
                            <span>
                              {item.payerAccountNumber ?? ''}{' '}
                              {item.payerFirstName ?? ''}{' '}
                              {item.payerLastName ?? ''}{' '}
                            </span>
                          ))}
                      </Col>
                      <Col>
                        {item != null && item.webShopCustomer !== null && (
                          <>
                            <span className="fw-bold small">
                              Verkkokaupan asiakas:
                            </span>
                            <br />
                            <div>
                              {item.webShopCustomer.firstName ?? ''}{' '}
                              {item.webShopCustomer.lastName ?? ''} <br />
                              {item.webShopCustomer.companyName ?? ''} <br />
                              {item.webShopCustomer.email ?? ''} <br />
                              {item.webShopCustomer.streetAddress ?? ''} <br />
                              {item.webShopCustomer.postalCode ?? ''}{' '}
                              {item.webShopCustomer.postCity ?? ''}
                              <br />
                              {item.webShopCustomer.mobileNumber ?? ''} <br />
                              {item.webShopCustomer.phoneNumber ?? ''}{' '}
                            </div>
                          </>
                        )}
                        {item != null &&
                          item.travelAgencyData !== null &&
                          item.travelAgencyData.finalClientFirstName !== null &&
                          item.travelAgencyData.finalClientFirstName !== '' && (
                            <>
                              <span className="fw-bold small">
                                {t(
                                  'bookingDetails:bookingInfo.customer.travelAgencyClient',
                                )}
                                :
                              </span>
                              <br />
                              <div>
                                {item.travelAgencyData.finalClientFirstName ??
                                  ''}{' '}
                                {item.travelAgencyData.finalClientLastName ??
                                  ''}{' '}
                                <br />
                                {item.travelAgencyData.finalClientCompany && (
                                  <>
                                    {item.travelAgencyData.finalClientCompany}{' '}
                                    <br />
                                  </>
                                )}
                                {item.travelAgencyData
                                  .finalClientStreetAddress && (
                                  <>
                                    {
                                      item.travelAgencyData
                                        .finalClientStreetAddress
                                    }
                                    <br />
                                  </>
                                )}
                                {item.travelAgencyData.finalClientZipCode && (
                                  <>
                                    {item.travelAgencyData.finalClientZipCode}
                                    <br />
                                  </>
                                )}
                                {item.travelAgencyData.finalClientCity && (
                                  <>
                                    {item.travelAgencyData.finalClientCity}
                                    <br />
                                  </>
                                )}
                                {item.travelAgencyData.finalClientPhone && (
                                  <>
                                    {item.travelAgencyData.finalClientPhone}
                                    <br />
                                  </>
                                )}
                                {item.travelAgencyData.finalClientEmail && (
                                  <>
                                    {item.travelAgencyData.finalClientEmail}
                                    <br />
                                  </>
                                )}
                                {countries != null && (
                                  <>
                                    {countries.find(
                                      country =>
                                        country.id.toLowerCase() ===
                                        item.travelAgencyData.finalClientCountry.toLowerCase(),
                                    )?.name ??
                                      t(
                                        'bookingDetails:bookingInfo.customer.noCountrySelected',
                                      )}
                                    <br />
                                  </>
                                )}
                              </div>
                            </>
                          )}
                      </Col>
                      <Col>
                        {item != null &&
                          item.travelAgencyAccountData !== null && (
                            <>
                              <span className="fw-bold small">
                                {t(
                                  'bookingDetails:bookingInfo.customer.travelAgencyUser',
                                )}
                                :
                              </span>
                              <br />
                              <div>
                                {item.travelAgencyAccountData
                                  .finalClientFirstName ?? ''}{' '}
                                {item.travelAgencyAccountData
                                  .finalClientLastName ?? ''}{' '}
                                <br />
                                {item.travelAgencyAccountData
                                  .finalClientCompany && (
                                  <>
                                    {
                                      item.travelAgencyAccountData
                                        .finalClientCompany
                                    }{' '}
                                    <br />
                                  </>
                                )}
                                {
                                  item.travelAgencyAccountData
                                    .finalClientStreetAddress
                                }{' '}
                                <br />
                                {
                                  item.travelAgencyAccountData
                                    .finalClientZipCode
                                }{' '}
                                {item.travelAgencyAccountData.finalClientCity}{' '}
                                <br />
                                {item.travelAgencyAccountData
                                  .travelAgencyPhone ?? ''}{' '}
                                <br />
                                {item.travelAgencyAccountData
                                  .travelAgencyEmail ?? ''}{' '}
                                <br />
                                {countries != null && (
                                  <>
                                    {countries.find(
                                      country =>
                                        country.id.toLowerCase() ===
                                        item.travelAgencyData.finalClientCountry.toLowerCase(),
                                    )?.name ??
                                      t(
                                        'bookingDetails:bookingInfo.customer.noCountrySelected',
                                      )}
                                    <br />
                                  </>
                                )}
                              </div>
                            </>
                          )}
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>

              <hr />

              <Heading
                Title={t('bookingDetails:bookingInfo.reservedItems.title')}
                Level={4}
                CssClass="mb-24"
              />
              {!isTravelAgencyUser && item.accommodationReclamationText && (
                <Alert className="mb-32">
                  <div>
                    {t('bookingDetails:bookingInfo.reservedItems.noticeTitle')}:
                  </div>
                  {item.accommodationReclamationText ?? ''}
                </Alert>
              )}
              <div className="table-responsive-lg">
                <Table bordered className="table-data table-wrap mt-16 mb-32">
                  <thead>
                    <tr key="bookingItemsHeader">
                      <th>
                        {t(
                          'bookingDetails:bookingInfo.orderItems.table.propertyService',
                        )}
                      </th>
                      {!isTravelAgencyUser && (
                        <th>
                          {t(
                            'bookingDetails:bookingInfo.orderItems.table.owner',
                          )}
                        </th>
                      )}
                      {!isTravelAgencyUser && (
                        <th>
                          {t(
                            'bookingDetails:bookingInfo.orderItems.table.caretakers',
                          )}
                        </th>
                      )}
                      <th>
                        {t('bookingDetails:bookingInfo.orderItems.table.start')}
                      </th>
                      <th>
                        {t('bookingDetails:bookingInfo.orderItems.table.end')}
                      </th>
                      <th>
                        {t(
                          'bookingDetails:bookingInfo.orderItems.table.quantity',
                        )}
                      </th>
                      <th>
                        {t(
                          'bookingDetails:bookingInfo.orderItems.table.adults',
                        )}
                      </th>
                      <th>
                        {t(
                          'bookingDetails:bookingInfo.orderItems.table.children',
                        )}
                      </th>
                      <th>
                        {t('bookingDetails:bookingInfo.orderItems.table.price')}
                      </th>
                      {!isTravelAgencyUser && (
                        <>
                          <th>
                            {t(
                              'bookingDetails:bookingInfo.orderItems.table.purchasePrice',
                            )}
                          </th>
                          <th>
                            {t(
                              'bookingDetails:bookingInfo.orderItems.table.salesCommission',
                            )}
                          </th>
                          <th>
                            {t(
                              'bookingDetails:bookingInfo.orderItems.table.discount',
                            )}
                          </th>
                          <th>
                            {t(
                              'bookingDetails:bookingInfo.orderItems.table.travelAgencyCommission',
                            )}
                          </th>
                          <th>
                            {t(
                              'bookingDetails:bookingInfo.orderItems.table.resellerCommission',
                            )}
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {item.bookingOrderItems.map((orderItem, orderIndex) => (
                      <tr
                        key={generateItemKey(
                          orderIndex,
                          orderItem.id.toString(),
                        )}
                      >
                        {orderItem.isCancelled && (
                          <td style={{ backgroundColor: '#ffcccc' }}>
                            {orderItem.addOnNumber !== null &&
                            orderItem.addOnNumber > 0
                              ? orderItem.addOnNumber
                              : ''}{' '}
                            {orderItem.addOnNumber !== null &&
                            orderItem.addOnNumber > 0
                              ? orderItem.itemName
                              : ''}{' '}
                            {orderItem.orderItemType !== null &&
                              orderItem.orderItemType !== undefined &&
                              orderItem.orderItemType === 0 && (
                                <button
                                  key={`accommodation-${orderItem.accommodationId}`}
                                  type="submit"
                                  disabled={isTravelAgencyUser}
                                  className="booking-link bg-transparent p-0 text-start border-0"
                                  onClick={e => {
                                    e.preventDefault();
                                    if (isTravelAgencyUser) return; // Extra safety
                                    window.open(
                                      `/accommodations/details/${orderItem.accommodationId}`,
                                      '_blank',
                                      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                    );
                                  }}
                                >
                                  {orderItem.accommodationNumber > 0
                                    ? orderItem.accommodationNumber
                                    : ''}{' '}
                                  {orderItem.itemName ?? ''}
                                </button>
                              )}
                            {orderItem.includedInPrice && (
                              <div className="text-danger">
                                {t(
                                  'bookingDetails:bookingInfo.orderItems.includedInPrice',
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {!orderItem.isCancelled && (
                          <td>
                            {orderItem.addOnNumber !== null &&
                            orderItem.addOnNumber > 0
                              ? orderItem.addOnNumber
                              : ''}{' '}
                            {orderItem.addOnNumber !== null &&
                            orderItem.addOnNumber > 0
                              ? orderItem.itemName
                              : ''}{' '}
                            {!isTravelAgencyUser &&
                              orderItem.orderItemType !== null &&
                              orderItem.orderItemType !== undefined &&
                              orderItem.orderItemType === 0 && (
                                <button
                                  key={`accommodation2-${orderItem.accommodationId}`}
                                  type="submit"
                                  disabled={isTravelAgencyUser}
                                  className="booking-link bg-transparent p-0 text-start border-0"
                                  onClick={e => {
                                    e.preventDefault();
                                    if (isTravelAgencyUser) return; // Extra safety
                                    window.open(
                                      `/accommodations/details/${orderItem.accommodationId}`,
                                      '_blank',
                                      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                    );
                                  }}
                                >
                                  {orderItem.accommodationNumber > 0
                                    ? orderItem.accommodationNumber
                                    : ''}{' '}
                                  {orderItem.itemName ?? ''}
                                </button>
                              )}
                            {isTravelAgencyUser &&
                              orderItem.orderItemType !== null &&
                              orderItem.orderItemType !== undefined &&
                              orderItem.orderItemType === 0 && (
                                <span>
                                  {orderItem.accommodationNumber > 0
                                    ? orderItem.accommodationNumber
                                    : ''}{' '}
                                  {orderItem.itemName ?? ''}
                                </span>
                              )}
                            {orderItem.includedInPrice && (
                              <div className="text-danger">
                                {t(
                                  'bookingDetails:bookingInfo.orderItems.includedInPrice',
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {!isTravelAgencyUser && (
                          <td>
                            {!item.crossSiteBooking &&
                              orderItem.ownerAccountId != null && (
                                <button
                                  key={orderItem.ownerAccountId}
                                  type="submit"
                                  className="booking-link text-start border-0"
                                  onClick={e => {
                                    e.preventDefault();
                                    window.open(
                                      `/users/details/${orderItem.ownerAccountId}`,
                                      '_blank',
                                      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                    );
                                  }}
                                >
                                  {orderItem.ownerName}
                                </button>
                              )}
                            {item.crossSiteBooking &&
                              orderItem.realOwnerAccountId != null && (
                                <button
                                  key={orderItem.realOwnerAccountId}
                                  type="submit"
                                  className="booking-link   text-start border-0"
                                  onClick={e => {
                                    e.preventDefault();
                                    window.open(
                                      `/users/details/${orderItem.realOwnerAccountId}`,
                                      '_blank',
                                      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                    );
                                  }}
                                >
                                  {orderItem.realOwnerName}
                                </button>
                              )}
                          </td>
                        )}
                        {!isTravelAgencyUser && (
                          <td>
                            {orderItem.careTakers !== undefined &&
                              orderItem.careTakers !== null &&
                              orderItem.careTakers.map(
                                (caretaker, indexingItem) => (
                                  <button
                                    key={`caretaker33-${caretaker.id}`}
                                    type="submit"
                                    className="booking-link bg-transparent p-0 text-start border-0"
                                    onClick={e => {
                                      e.preventDefault();
                                      window.open(
                                        `/users/details/${caretaker.id}`,
                                        '_blank',
                                        `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                      );
                                    }}
                                  >
                                    {caretaker.ownerNameNumber}
                                  </button>
                                ),
                              )}
                          </td>
                        )}
                        <td>
                          {orderItem.orderItemType !== 2
                            ? new Date(
                                Date.parse(orderItem.startDateTime),
                              ).toLocaleString('fi-FI', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : new Date(
                                Date.parse(orderItem.arrivalDateTime),
                              ).toLocaleString('fi-FI', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}{' '}
                        </td>
                        <td>
                          {orderItem.orderItemType !== 2
                            ? new Date(
                                Date.parse(orderItem.endDateTime),
                              ).toLocaleString('fi-FI', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : new Date(
                                Date.parse(orderItem.departureDateTime),
                              ).toLocaleString('fi-FI', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}{' '}
                        </td>
                        <td>{orderItem.units} </td>
                        <td>{orderItem.amountOfAdults} </td>
                        <td>{orderItem.amountOfChildren} </td>
                        {!item.crossSiteBooking && !isTravelAgencyUser && (
                          <td>{orderItem.pricesResponse.grossPrice}</td>
                        )}
                        {!item.crossSiteBooking && isTravelAgencyUser && (
                          <td>{orderItem.pricesResponse.netPrice}</td>
                        )}
                        {item.crossSiteBooking && (
                          <td>
                            {orderItem.purchasePricesResponse.sitePurchasePrice}
                          </td>
                        )}
                        {!isTravelAgencyUser && !item.crossSiteBooking && (
                          <td>
                            {orderItem.purchasePricesResponse
                              .sitePurchasePrice !== undefined &&
                            orderItem.purchasePricesResponse.sitePurchasePrice >
                              0
                              ? `${orderItem.purchasePricesResponse.sitePurchasePrice}(${orderItem.purchasePricesResponse.ownerPurchasePrice})`
                              : orderItem.purchasePricesResponse
                                  .ownerPurchasePrice}{' '}
                          </td>
                        )}
                        {!isTravelAgencyUser && item.crossSiteBooking && (
                          <td>
                            {orderItem.purchasePricesResponse
                              .ownerPurchasePrice !== undefined &&
                            orderItem.purchasePricesResponse
                              .ownerPurchasePrice > 0
                              ? `${orderItem.purchasePricesResponse.ownerPurchasePrice}(${orderItem.purchasePricesResponse.sitePurchasePrice})`
                              : orderItem.purchasePricesResponse
                                  .ownerPurchasePrice}{' '}
                          </td>
                        )}
                        {!isTravelAgencyUser && (
                          <>
                            <td>
                              {orderItem.pricesResponse.sellerCommission}{' '}
                            </td>
                            <td>
                              {orderItem.pricesResponse.discount}(
                              {orderItem.pricesResponse.discountP}
                              %){' '}
                            </td>
                            <td>
                              {orderItem.pricesResponse.travelAgencyComission}(
                              {orderItem.pricesResponse.travelAgencyComissionP}
                              %){' '}
                            </td>
                          </>
                        )}
                        {!isTravelAgencyUser && (
                          <td>
                            {orderItem.pricesResponse.travelAgencyOnlyComission}
                          </td>
                        )}
                        {!isTravelAgencyUser &&
                          orderItem.optiwattiData !== null && (
                            <td>
                              Optiwatti status:
                              {orderItem.optiwattiData.translatedStatus}{' '}
                            </td>
                          )}
                      </tr>
                    ))}

                    {item.extraBookingResponses !== null &&
                      item.extraBookingResponses.length > 0 && (
                        <tr key={`extraBookingResponses${item.id}`}>
                          <td
                            style={{
                              border: 'none',
                              backgroundColor: 'lightgray',
                            }}
                          >
                            {item.isAddOnBooking === false && (
                              <h5 className="table-danger">
                                {t(
                                  'bookingDetails:bookingInfo.additionalBookings.additionalBookings',
                                )}
                                :
                              </h5>
                            )}
                            {item.isAddOnBooking === true && (
                              <h5 className="table-danger">
                                {t(
                                  'bookingDetails:bookingInfo.additionalBookings.mainBooking',
                                )}
                                :
                              </h5>
                            )}
                          </td>
                          <td
                            style={{
                              border: 'none',
                              backgroundColor: 'lightgray',
                            }}
                          >
                            <div className="d-flex flex-column flex-lg-row gap-8 gap-lg-40">
                              {item.extraBookingResponses.map(
                                (extraBooking, extraIndex) => (
                                  <div>
                                    <button
                                      key={`extraBooking-${extraBooking.id}`}
                                      type="submit"
                                      className={`booking-link text-start border-0 ${extraBooking.isCancelled ? 'bg-danger bg-opacity-0' : ''}`}
                                      onClick={e => {
                                        e.preventDefault();
                                        window.open(
                                          `/bookings/${extraBooking.id}`,
                                          '_blank',
                                          `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`,
                                        );
                                      }}
                                    >
                                      <span>
                                        {`${extraBooking.bookingNumber} ${getBookingTitle(extraBooking)}`}
                                      </span>
                                    </button>
                                  </div>
                                ),
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    {!isTravelAgencyUser && (
                      <tr>
                        <td style={{ textAlign: 'right' }} colSpan={11}>
                          {t(
                            'bookingDetails:bookingInfo.totals.resellerTotalAmount',
                          )}
                        </td>
                        <td>{item ? item.travelAgencyNetPrice : ''}</td>
                        <td style={{ textAlign: 'right' }} colSpan={13}>
                          {t(
                            'bookingDetails:bookingInfo.totals.bookingTotalAmount',
                          )}
                        </td>
                        <td>{item ? item.netPrice : ''}</td>
                      </tr>
                    )}
                    {isTravelAgencyUser && (
                      <tr>
                        <td style={{ textAlign: 'right' }} colSpan={13}>
                          {t(
                            'bookingDetails:bookingInfo.totals.bookingTotalAmount',
                          )}
                        </td>
                        <td>{item ? item.travelAgencyNetPrice : ''}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <hr />

              {!isTravelAgencyUser && (
                <Heading
                  Level={4}
                  Title={t('bookingDetails:bookingInfo.invoices.title')}
                />
              )}
              {!isTravelAgencyUser &&
                (item.bookingInvoiceItems === null ||
                  item.bookingInvoiceItems.length <= 0) && (
                  <p className="mt-16 mb-32">
                    {t('bookingDetails:bookingInfo.invoices.noInvoices')}
                  </p>
                )}
              <Accordion defaultActiveKey="0">
                {!isTravelAgencyUser &&
                  item.bookingInvoiceItems !== null &&
                  item.bookingInvoiceItems.filter(oi =>
                    oi.discriminator.includes('Advance'),
                  ).length > 0 && (
                    <Accordion.Item
                      key={`accordion-item-advance-single-${item.id}`}
                      eventKey="1"
                    >
                      <Accordion.Header>
                        {t(
                          'bookingDetails:bookingInfo.invoices.advanceInvoices',
                        )}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Table
                          bordered
                          className="table-data table-wrap mt-16 mb-32"
                        >
                          <thead>
                            <tr key="advanceInvoicesHeader-1">
                              <th className="h1">
                                {t(
                                  'bookingDetails:bookingInfo.invoices.table.invoiceNumber',
                                )}
                              </th>
                              <th className="h1">
                                {t(
                                  'bookingDetails:bookingInfo.invoices.table.status',
                                )}
                              </th>
                              <th className="h1">
                                {t(
                                  'bookingDetails:bookingInfo.invoices.table.dueDate',
                                )}
                              </th>
                              <th className="h1">
                                {t(
                                  'bookingDetails:bookingInfo.invoices.table.invoiceDate',
                                )}
                              </th>
                              <th className="h1">
                                {t(
                                  'bookingDetails:bookingInfo.invoices.table.referenceNumber',
                                )}
                              </th>
                              <th className="h1">
                                {t(
                                  'bookingDetails:bookingInfo.invoices.table.amount',
                                )}
                              </th>
                              <th className="h1">Avoin saldo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!isTravelAgencyUser &&
                              item.bookingInvoiceItems
                                .filter(oi =>
                                  oi.discriminator.includes('Advance'),
                                )
                                .map((orderItem, orderIndex) => (
                                  <tr
                                    key={generateItemKey(
                                      orderIndex,
                                      orderItem.number.toString(),
                                    )}
                                  >
                                    <td>
                                      <span className="th">
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.labels.propertyService',
                                        )}
                                      </span>
                                      <button
                                        className="booking-link  text-start border-0"
                                        type="submit"
                                        onClick={() =>
                                          getPdfInvoice(
                                            orderItem.id,
                                            false,
                                            false,
                                            'fi',
                                            true,
                                          )
                                        }
                                      >
                                        {orderItem.number ?? ''}{' '}
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.actions.openInvoiceFi',
                                        )}
                                      </button>
                                      <br />
                                      <button
                                        className="booking-link text-start border-0"
                                        type="submit"
                                        onClick={() =>
                                          getPdfInvoice(
                                            orderItem.id,
                                            false,
                                            false,
                                            'en',
                                            true,
                                          )
                                        }
                                      >
                                        {orderItem.number ?? ''}{' '}
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.actions.openInvoiceEn',
                                        )}
                                      </button>
                                      <br />
                                      <button
                                        className="booking-link text-start border-0"
                                        type="submit"
                                        onClick={() =>
                                          getPdfInvoice(
                                            orderItem.id,
                                            true,
                                            false,
                                            'fi',
                                            true,
                                          )
                                        }
                                      >
                                        {orderItem.number ?? ''}{' '}
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                        )}
                                      </button>
                                      <br />
                                      <button
                                        className="booking-link text-start border-0"
                                        type="submit"
                                        onClick={() =>
                                          getPdfInvoice(
                                            orderItem.id,
                                            true,
                                            false,
                                            'en',
                                            true,
                                          )
                                        }
                                      >
                                        {orderItem.number ?? ''}{' '}
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                        )}
                                      </button>
                                    </td>
                                    <td>
                                      <span className="th">
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.labels.name',
                                        )}
                                      </span>
                                      {orderItem.compensationInvoiceId
                                        ? t(
                                            'bookingDetails:bookingInfo.invoices.status.credited',
                                          )
                                        : t(
                                            'bookingDetails:bookingInfo.invoices.status.notCredited',
                                          )}
                                    </td>
                                    <td>
                                      <span className="th">
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.labels.time',
                                        )}
                                      </span>
                                      {new Date(
                                        Date.parse(orderItem.dueDate),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}{' '}
                                    </td>
                                    <td>
                                      <span className="th">
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.labels.adults',
                                        )}
                                      </span>
                                      {new Date(
                                        Date.parse(orderItem.invoiceDate),
                                      ).toLocaleString('fi-FI', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}{' '}
                                    </td>
                                    <td>
                                      <span className="th">
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.labels.children',
                                        )}
                                      </span>
                                      {orderItem.referenceNumber}{' '}
                                    </td>
                                    <td>
                                      <span className="th">
                                        {t(
                                          'bookingDetails:bookingInfo.invoices.labels.children',
                                        )}
                                      </span>
                                      {item.advanceBillPrice
                                        ? item.advanceBillPrice
                                        : ''}{' '}
                                    </td>
                                    <td>
                                      <span className="th">Avoin saldo</span>
                                      {orderItem.openBalance &&
                                      orderItem.openBalance > 0 ? (
                                        <span style={{ color: 'red' }}>
                                          {orderItem.openBalance.toLocaleString(
                                            'fi-FI',
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            },
                                          )}
                                          €
                                        </span>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                  </tr>
                                ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                {!isTravelAgencyUser &&
                  item.bookingInvoiceItems !== null &&
                  item.bookingInvoiceItems.filter(oi =>
                    oi.discriminator.includes('Final'),
                  ).length > 0 && (
                    <Accordion.Item
                      key={`accordion-item-final-single-${item.id}`}
                      eventKey="2"
                    >
                      <Accordion.Header>
                        {t('bookingDetails:bookingInfo.invoices.finalInvoices')}
                      </Accordion.Header>
                      <Accordion.Body>
                        {!isTravelAgencyUser &&
                          item.bookingInvoiceItems !== null &&
                          item.bookingInvoiceItems.filter(oi =>
                            oi.discriminator.includes('Final'),
                          ).length > 0 && (
                            <Table
                              bordered
                              className="table-data table-wrap mt-16 mb-32"
                            >
                              <thead>
                                <tr key="finalInvoicesHeader-2">
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.invoiceNumber',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.status',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.dueDate',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.invoiceDate',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.referenceNumber',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.amount',
                                    )}
                                  </th>
                                  <th className="h1">Avoin saldo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {!isTravelAgencyUser &&
                                  item.bookingInvoiceItems
                                    .filter(oi =>
                                      oi.discriminator.includes('Final'),
                                    )
                                    .map((orderItem, orderIndex) => (
                                      <tr
                                        key={generateItemKey(
                                          orderIndex,
                                          orderItem.number != null
                                            ? orderItem.number.toString()
                                            : '',
                                        )}
                                      >
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.propertyService',
                                            )}
                                          </span>
                                          {!item.crossSiteBooking && (
                                            <>
                                              {/* <button */}
                                              {/*  type="submit" */}
                                              {/*  className="booking-link  text-start border-0" */}
                                              {/*  onClick={e => { */}
                                              {/*    e.preventDefault(); */}
                                              {/*    window.open( */}
                                              {/*      `/bookings/fi/${item.id}/invoice/${orderItem.number}/false`, */}
                                              {/*      '_blank', */}
                                              {/*      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`, */}
                                              {/*    ); */}
                                              {/*  }} */}
                                              {/* > */}
                                              {/*  {orderItem.number ?? ''} avaa lasku */}
                                              {/*  fi */}
                                              {/* </button> */}
                                              <button
                                                type="submit"
                                                className="booking-link text-start border-0"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    false,
                                                    false,
                                                    'fi',
                                                    false,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceFi',
                                                )}
                                              </button>
                                              {/* <button */}
                                              {/*  type="submit" */}
                                              {/*  className="booking-link  text-start border-0" */}
                                              {/*  onClick={e => { */}
                                              {/*    e.preventDefault(); */}
                                              {/*    window.open( */}
                                              {/*      `/bookings/en/${item.id}/invoice/${orderItem.number}/false`, */}
                                              {/*      '_blank', */}
                                              {/*      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`, */}
                                              {/*    ); */}
                                              {/*  }} */}
                                              {/* > */}
                                              {/*  {orderItem.number ?? ''} avaa lasku */}
                                              {/*  en */}
                                              {/* </button> */}
                                              <button
                                                type="submit"
                                                className="booking-link text-start border-0"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    false,
                                                    false,
                                                    'en',
                                                    false,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceEn',
                                                )}
                                              </button>
                                              <br />
                                              {/* <button */}
                                              {/*  type="submit" */}
                                              {/*  className="booking-link text-start border-0" */}
                                              {/*  onClick={e => { */}
                                              {/*    e.preventDefault(); */}
                                              {/*    window.open( */}
                                              {/*      `/bookings/fi/${item.id}/invoice/${orderItem.number}/true`, */}
                                              {/*      '_blank', */}
                                              {/*      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`, */}
                                              {/*    ); */}
                                              {/*  }} */}
                                              {/* > */}
                                              {/*  {orderItem.number ?? ''} avaa lasku */}
                                              {/*  fi(kopio) */}
                                              {/* </button> */}
                                              <button
                                                type="submit"
                                                className="booking-link text-start border-0"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    true,
                                                    false,
                                                    'fi',
                                                    false,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                                )}
                                              </button>
                                              <br />
                                              {/* <button */}
                                              {/*  type="submit" */}
                                              {/*  className="booking-link text-start border-0" */}
                                              {/*  onClick={e => { */}
                                              {/*    e.preventDefault(); */}
                                              {/*    window.open( */}
                                              {/*      `/bookings/en/${item.id}/invoice/${orderItem.number}/true`, */}
                                              {/*      '_blank', */}
                                              {/*      `width=1000,height=1000,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 500}`, */}
                                              {/*    ); */}
                                              {/*  }} */}
                                              {/* > */}
                                              {/*  {orderItem.number ?? ''} avaa lasku */}
                                              {/*  en(kopio) */}
                                              {/* </button> */}
                                              <button
                                                type="submit"
                                                className="booking-link text-start border-0"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    true,
                                                    false,
                                                    'en',
                                                    false,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                                )}
                                              </button>
                                            </>
                                          )}
                                          {item.crossSiteBooking && (
                                            <button
                                              type="submit"
                                              className="booking-link  text-start border-0"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  true,
                                                  false,
                                                  'fi',
                                                  false,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                              )}
                                            </button>
                                          )}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.name',
                                            )}
                                          </span>
                                          {orderItem.compensationInvoiceId
                                            ? t(
                                                'bookingDetails:bookingInfo.invoices.status.credited',
                                              )
                                            : t(
                                                'bookingDetails:bookingInfo.invoices.status.notCredited',
                                              )}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.time',
                                            )}
                                          </span>
                                          {new Date(
                                            Date.parse(orderItem.dueDate),
                                          ).toLocaleString('fi-FI', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}{' '}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.adults',
                                            )}
                                          </span>
                                          {new Date(
                                            Date.parse(orderItem.invoiceDate),
                                          ).toLocaleString('fi-FI', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}{' '}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.children',
                                            )}
                                          </span>
                                          {orderItem.referenceNumber ?? ''}{' '}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.children',
                                            )}
                                          </span>
                                          {item.crossSiteBooking &&
                                          item.finalBillPrice
                                            ? item.invoiceVisiblePrice
                                            : ''}
                                          {!item.crossSiteBooking &&
                                          item.finalBillPrice
                                            ? item.finalBillPrice
                                            : ''}{' '}
                                        </td>
                                        <td>
                                          <span className="th">
                                            Avoin saldo
                                          </span>
                                          {orderItem.openBalance &&
                                          orderItem.openBalance > 0 ? (
                                            <span style={{ color: 'red' }}>
                                              {orderItem.openBalance.toLocaleString(
                                                'fi-FI',
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              )}
                                              €
                                            </span>
                                          ) : (
                                            '—'
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                              </tbody>
                            </Table>
                          )}
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
                {!isTravelAgencyUser &&
                  item.bookingInvoiceItems !== null &&
                  item.bookingInvoiceItems.filter(oi =>
                    oi.discriminator.includes('Compensation'),
                  ).length > 0 && (
                    <Accordion.Item
                      eventKey="3"
                      key={`accordion-item-compensation-single-${item.id}`}
                    >
                      <Accordion.Header>
                        {t(
                          'bookingDetails:bookingInfo.invoices.creditInvoices',
                        )}
                      </Accordion.Header>
                      <Accordion.Body>
                        {!isTravelAgencyUser &&
                          item.bookingInvoiceItems !== null &&
                          item.bookingInvoiceItems.filter(oi =>
                            oi.discriminator.includes('Compensation'),
                          ).length > 0 && (
                            <Table
                              bordered
                              className="table-data table-wrap mt-16 mb-32"
                            >
                              <thead>
                                <tr key="compensationInvoicesHeader-3">
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.invoiceNumber',
                                    )}
                                  </th>
                                  {/* <th className="h1"> */}
                                  {/*  {t( */}
                                  {/*    'bookingDetails:bookingInfo.invoices.table.status', */}
                                  {/*  )} */}
                                  {/* </th> */}
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.dueDate',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.invoiceDate',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.referenceNumber',
                                    )}
                                  </th>
                                  <th className="h1">
                                    {t(
                                      'bookingDetails:bookingInfo.invoices.table.amount',
                                    )}
                                  </th>
                                  <th className="h1">Avoin saldo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {!isTravelAgencyUser &&
                                  item.bookingInvoiceItems
                                    .filter(oi =>
                                      oi.discriminator.includes('Compensation'),
                                    )
                                    .map((orderItem, orderIndex) => (
                                      <tr
                                        key={generateItemKey(
                                          orderIndex,
                                          orderItem.number != null
                                            ? orderItem.number.toString()
                                            : '',
                                        )}
                                      >
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.propertyService',
                                            )}
                                          </span>
                                          {!item.crossSiteBooking && (
                                            <>
                                              <button
                                                className="booking-link  text-start border-0"
                                                type="submit"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    false,
                                                    true,
                                                    'fi',
                                                    orderItem.isAdvance,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceFi',
                                                )}{' '}
                                                {orderItem.isAdvance
                                                  ? t(
                                                      'bookingDetails:bookingInfo.invoices.types.advance',
                                                    )
                                                  : t(
                                                      'bookingDetails:bookingInfo.invoices.types.final',
                                                    )}
                                              </button>
                                              <br />
                                              <button
                                                className="booking-link  text-start border-0"
                                                type="submit"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    false,
                                                    true,
                                                    'en',
                                                    orderItem.isAdvance,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceEn',
                                                )}{' '}
                                                {orderItem.isAdvance
                                                  ? t(
                                                      'bookingDetails:bookingInfo.invoices.types.advance',
                                                    )
                                                  : t(
                                                      'bookingDetails:bookingInfo.invoices.types.final',
                                                    )}
                                              </button>
                                              <br />
                                              <button
                                                className="booking-link text-start border-0 border-0"
                                                type="submit"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    true,
                                                    true,
                                                    'fi',
                                                    orderItem.isAdvance,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                                )}
                                                {orderItem.isAdvance
                                                  ? t(
                                                      'bookingDetails:bookingInfo.invoices.types.advance',
                                                    )
                                                  : t(
                                                      'bookingDetails:bookingInfo.invoices.types.final',
                                                    )}
                                              </button>
                                              <br />
                                              <button
                                                className="booking-link text-start border-0 border-0"
                                                type="submit"
                                                onClick={() =>
                                                  getPdfInvoice(
                                                    orderItem.id,
                                                    true,
                                                    true,
                                                    'en',
                                                    orderItem.isAdvance,
                                                  )
                                                }
                                              >
                                                {orderItem.number ?? ''}{' '}
                                                {t(
                                                  'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                                )}
                                                {orderItem.isAdvance
                                                  ? t(
                                                      'bookingDetails:bookingInfo.invoices.types.advance',
                                                    )
                                                  : t(
                                                      'bookingDetails:bookingInfo.invoices.types.final',
                                                    )}
                                              </button>
                                            </>
                                          )}
                                          {item.crossSiteBooking && (
                                            <button
                                              className="booking-link text-start border-0 border-0"
                                              type="submit"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  true,
                                                  true,
                                                  'fi',
                                                  orderItem.isAdvance,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceFiCopy',
                                              )}
                                              {orderItem.isAdvance
                                                ? t(
                                                    'bookingDetails:bookingInfo.invoices.types.advance',
                                                  )
                                                : t(
                                                    'bookingDetails:bookingInfo.invoices.types.final',
                                                  )}
                                            </button>
                                          )}
                                          <br />
                                          {item.crossSiteBooking && (
                                            <button
                                              className="booking-link text-start border-0 border-0"
                                              type="submit"
                                              onClick={() =>
                                                getPdfInvoice(
                                                  orderItem.id,
                                                  true,
                                                  true,
                                                  'en',
                                                  orderItem.isAdvance,
                                                )
                                              }
                                            >
                                              {orderItem.number ?? ''}{' '}
                                              {t(
                                                'bookingDetails:bookingInfo.invoices.actions.openInvoiceEnCopy',
                                              )}
                                              {orderItem.isAdvance
                                                ? t(
                                                    'bookingDetails:bookingInfo.invoices.types.advance',
                                                  )
                                                : t(
                                                    'bookingDetails:bookingInfo.invoices.types.final',
                                                  )}
                                            </button>
                                          )}
                                        </td>
                                        {/* <td> */}
                                        {/*  <span className="th"> */}
                                        {/*    {t( */}
                                        {/*      'bookingDetails:bookingInfo.invoices.labels.name', */}
                                        {/*    )} */}
                                        {/*  </span> */}
                                        {/*  {orderItem.compensationInvoiceId */}
                                        {/*    ? t( */}
                                        {/*        'bookingDetails:bookingInfo.invoices.status.credited', */}
                                        {/*      ) */}
                                        {/*    : t( */}
                                        {/*        'bookingDetails:bookingInfo.invoices.status.notCredited', */}
                                        {/*      )} */}
                                        {/* </td> */}
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.time',
                                            )}
                                          </span>
                                          {new Date(
                                            Date.parse(orderItem.dueDate),
                                          ).toLocaleString('fi-FI', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}{' '}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.adults',
                                            )}
                                          </span>
                                          {new Date(
                                            Date.parse(orderItem.invoiceDate),
                                          ).toLocaleString('fi-FI', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}{' '}
                                        </td>
                                        <td>
                                          <span className="th">
                                            {t(
                                              'bookingDetails:bookingInfo.invoices.labels.children',
                                            )}
                                          </span>
                                          {orderItem.referenceNumber ?? ''}{' '}
                                        </td>
                                        {!item.crossSiteBooking && (
                                          <td>
                                            {orderItem.isAdvance &&
                                            item.advanceBillPrice
                                              ? -item.advanceBillPrice
                                              : ''}{' '}
                                            {!orderItem.isAdvance &&
                                            item.finalBillPrice
                                              ? -item.finalBillPrice
                                              : ''}{' '}
                                          </td>
                                        )}
                                        {item.crossSiteBooking && (
                                          <td>
                                            {item.invoiceVisiblePrice
                                              ? -item.invoiceVisiblePrice
                                              : ''}{' '}
                                          </td>
                                        )}
                                        <td>
                                          <span className="th">
                                            Avoin saldo
                                          </span>
                                          {orderItem.openBalance &&
                                          orderItem.openBalance > 0 ? (
                                            <span style={{ color: 'red' }}>
                                              {orderItem.openBalance.toLocaleString(
                                                'fi-FI',
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              )}
                                              €
                                            </span>
                                          ) : (
                                            '—'
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                              </tbody>
                            </Table>
                          )}
                      </Accordion.Body>
                    </Accordion.Item>
                  )}
              </Accordion>

              <hr />

              <Heading
                Level={4}
                Title={t('bookingDetails:bookingInfo.communication.title')}
              />
              <Table bordered className="table-data mt-16">
                <thead>
                  <tr key="customerBookingNotesHeader2">
                    <th className="ps-24">
                      {t(
                        'bookingDetails:bookingInfo.communication.notes.customer',
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!editCustomerNote &&
                    item.customerBookingNotes.map((orderItem, orderIndex) => (
                      <tr key={`modifycustomernote-${orderItem.id}`}>
                        <td
                          style={{
                            maxWidth: '200px',
                            minWidth: '200px',
                            wordWrap: 'break-word',
                          }}
                        >
                          {orderItem.noteText}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-blue"
                            type="submit"
                            onClick={() => {
                              setCustomerNoteModalBookingId(item.id);
                              setCustomerNoteModalNoteId(orderItem.id);
                              setEditCustomerNote(true);
                            }}
                          >
                            {t('common:buttons.modify')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  {item.customerBookingNotes.length === 0 && (
                    <tr>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-blue"
                          type="submit"
                          onClick={e =>
                            handleAddCustomerBookingNote(e, item.id, 'new')
                          }
                        >
                          {t(
                            'bookingDetails:bookingInfo.communication.notes.addCustomerNote',
                          )}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {!isTravelAgencyUser && (
                <>
                  <Table bordered className="table-data mt-16">
                    <thead>
                      <tr key="ownerBookingNotesHeader2">
                        <th className="ps-24">
                          {t(
                            'bookingDetails:bookingInfo.communication.notes.owner',
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {!editOwnerNote &&
                        item.ownerBookingNotes.map((orderItem, orderIndex) => (
                          <tr key={orderItem.id}>
                            <td
                              style={{
                                maxWidth: '200px',
                                minWidth: '200px',
                                wordWrap: 'break-word',
                              }}
                            >
                              {orderItem.noteText}
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-blue"
                                type="submit"
                                onClick={() => {
                                  setOwnerNoteModalBookingId(item.id);
                                  setOwnerNoteModalNoteId(orderItem.id);
                                  setEditOwnerNote(true);
                                }}
                              >
                                {t('common:buttons.modify')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      {item.ownerBookingNotes.length === 0 && (
                        <tr>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-blue"
                              type="submit"
                              onClick={e =>
                                handleAddOwnerBookingNote(e, item.id, 'new')
                              }
                            >
                              {t(
                                'bookingDetails:bookingInfo.communication.notes.addOwnerNote',
                              )}{' '}
                            </button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  <Table bordered className="table-data mt-16">
                    <thead>
                      <tr key="maintainerBookingNotesHeader2">
                        <th className="ps-24">
                          {t(
                            'bookingDetails:bookingInfo.communication.notes.clerk',
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {!editOfficerNote &&
                        item.officerBookingNotes.map(
                          (orderItem, orderIndex) => (
                            <tr key={orderItem.id}>
                              <td
                                style={{
                                  maxWidth: '200px',
                                  minWidth: '200px',
                                  wordWrap: 'break-word',
                                }}
                              >
                                {orderItem.noteText}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-blue"
                                  type="submit"
                                  onClick={() => {
                                    setOfficerNoteModalBookingId(item.id);
                                    setOfficerNoteModalNoteId(orderItem.id);
                                    setEditOfficerNote(true);
                                  }}
                                >
                                  {t('common:buttons.modify')}
                                </button>
                              </td>
                            </tr>
                          ),
                        )}
                      {item.officerBookingNotes.length === 0 && (
                        <tr>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-blue"
                              type="submit"
                              onClick={e =>
                                handleAddOfficerBookingNote(e, item.id, 'new')
                              }
                            >
                              {t(
                                'bookingDetails:bookingInfo.communication.notes.addClerkNote',
                              )}
                            </button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  {/* Suomulla tarviiko vain yhden viestin asiakas, ei vaiheittaisia? */}
                  {/*
                <div className="row my-48 gx-48">
                  <div className="col">
                    <h3 className="h4 mb-16">
                      Lähetä varausvahvistus asiakkaalle
                    </h3>
                    <Form.Label htmlFor="clientEmail" className="required">
                      Sähköpostiosoite
                    </Form.Label>
                    <InputGroup className="flex-nowrap">
                      <InputText
                        Id="clientEmail"
                        Name="clientEmail"
                        IsRequired
                        HandleChange={event =>
                          setClientBookingConfirmationToSentEmailAddress(
                            event.target.value,
                          )
                        }
                        Value={clientBookingConfirmationToSentEmailAddress}
                        ClassName="mb-0 flex-fill rounded-end-0"
                      />
                      <Button
                        onClick={() => sendClientBookingConfirmation()}
                        disabled={
                          !isValidEmail(
                            clientBookingConfirmationToSentEmailAddress,
                          )
                        }
                        className="ms-n8 px-16"
                      >
                        Lähetä
                      </Button>
                    </InputGroup>
                  </div>
                  <div className="col">
                    <BookingOwnersEmailComponent
                      bookingOwners={bookingOwners} // Pass bookingOwners to the child
                      sendOwnerBookingConfirmation={
                        sendOwnerBookingConfirmation
                      } // Pass the send function to the child
                    />
                  </div>
                  <div className="col">
                    <BookingMaintainersEmailComponent
                      bookingMaintainers={bookingMaintainers}
                      sendMaintainerBookingConfirmation={
                        sendMaintainerBookingConfirmation
                      }
                    />
                  </div>
                </div>
                */}
                  {/* Tarkistus  että bookingilla on payerId, muuten ei voida mitään viestiä lähettää */}
                  {/* Lisävarauksesta itsesätään ei myöskään lähetetä mitään vaihe viestejä asiakkaalle */}
                  {showPhase123EmailBoxes(item) && (
                    <div className="row my-48 gx-48">
                      <div className="col-md-4">
                        <h3 className="h4 mb-16">
                          {t(
                            'bookingDetails:bookingInfo.messaging.phase1.title',
                          )}
                        </h3>
                        <Form.Label
                          htmlFor="clientEmailPhase1"
                          className="required"
                        >
                          {t(
                            'bookingDetails:bookingInfo.messaging.phase1.emailLabel',
                          )}
                        </Form.Label>
                        <InputGroup className="flex-nowrap">
                          <InputText
                            Id="clientEmailPhase1"
                            Name="clientEmailPhase1"
                            IsRequired
                            HandleChange={event =>
                              setClientBookingPhase1ToSentEmailAddress(
                                event.target.value,
                              )
                            }
                            Value={clientBookingPhase1ToSentEmailAddress}
                            ClassName="mb-0 flex-fill rounded-end-0"
                          />
                          <Button
                            onClick={() =>
                              sendClientAnyPhaseConfirmation(
                                ClientMessagePhase.Phase1,
                                item.id,
                              )
                            }
                            disabled={
                              !isValidEmails(
                                clientBookingPhase1ToSentEmailAddress,
                              )
                            }
                            className="ms-n8 px-16"
                          >
                            {t('bookingDetails:bookingInfo.messaging.send')}
                          </Button>
                        </InputGroup>
                      </div>
                      {item.sellerSiteId !==
                        '9ECB7F14-13A6-4F29-A3EE-0FCF1977DAEF' && (
                        <>
                          <div className="col-md-4">
                            <h3 className="h4 mb-16">
                              {t(
                                'bookingDetails:bookingInfo.messaging.phase2.title',
                              )}
                            </h3>
                            <Form.Label
                              htmlFor="clientEmailPhase2"
                              className="required"
                            >
                              {t(
                                'bookingDetails:bookingInfo.messaging.phase2.emailLabel',
                              )}
                            </Form.Label>
                            <InputGroup className="flex-nowrap">
                              <InputText
                                Id="clientEmailPhase2"
                                Name="clientEmailPhase2"
                                IsRequired
                                HandleChange={event =>
                                  setClientBookingPhase2ToSentEmailAddress(
                                    event.target.value,
                                  )
                                }
                                Value={clientBookingPhase2ToSentEmailAddress}
                                ClassName="mb-0 flex-fill rounded-end-0"
                              />
                              <Button
                                onClick={() =>
                                  sendClientAnyPhaseConfirmation(
                                    ClientMessagePhase.Phase2,
                                    item.id,
                                  )
                                }
                                disabled={
                                  !isValidEmails(
                                    clientBookingPhase2ToSentEmailAddress,
                                  )
                                }
                                className="ms-n8 px-16"
                              >
                                {t('bookingDetails:bookingInfo.messaging.send')}
                              </Button>
                            </InputGroup>
                          </div>
                          <div className="col-md-4">
                            <h3 className="h4 mb-16">
                              {t(
                                'bookingDetails:bookingInfo.messaging.phase3.title',
                              )}
                            </h3>
                            <Form.Label
                              htmlFor="clientEmailPhase3"
                              className="required"
                            >
                              {t(
                                'bookingDetails:bookingInfo.messaging.phase3.emailLabel',
                              )}
                            </Form.Label>
                            <InputGroup className="flex-nowrap">
                              <InputText
                                Id="clientEmailPhase3"
                                Name="clientEmailPhase3"
                                IsRequired
                                HandleChange={event =>
                                  setClientBookingPhase3ToSentEmailAddress(
                                    event.target.value,
                                  )
                                }
                                Value={clientBookingPhase3ToSentEmailAddress}
                                ClassName="mb-0 flex-fill rounded-end-0"
                              />
                              <Button
                                onClick={() =>
                                  sendClientAnyPhaseConfirmation(
                                    ClientMessagePhase.Phase3,
                                    item.id,
                                  )
                                }
                                disabled={
                                  !isValidEmails(
                                    clientBookingPhase3ToSentEmailAddress,
                                  )
                                }
                                className="ms-n8 px-16"
                              >
                                {t('bookingDetails:bookingInfo.messaging.send')}
                              </Button>
                            </InputGroup>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {showEmailConfirmationSuomu(item) && (
                    <div className="col-md-4">
                      <h3 className="h4 mb-16">
                        Lähetä varausvahvistus asiakkaalle
                      </h3>
                      <Form.Label
                        htmlFor="clientEmailPhase1"
                        className="required"
                      >
                        {t(
                          'bookingDetails:bookingInfo.messaging.phase1.emailLabel',
                        )}
                      </Form.Label>
                      <InputGroup className="flex-nowrap">
                        <InputText
                          Id="clientEmailPhase1"
                          Name="clientEmailPhase1"
                          IsRequired
                          HandleChange={event =>
                            setClientBookingPhase1ToSentEmailAddress(
                              event.target.value,
                            )
                          }
                          Value={clientBookingPhase1ToSentEmailAddress}
                          ClassName="mb-0 flex-fill rounded-end-0"
                        />
                        <Button
                          onClick={() =>
                            sendClientAnyPhaseConfirmation(
                              ClientMessagePhase.Phase1,
                              item.id,
                            )
                          }
                          disabled={
                            !isValidEmails(
                              clientBookingPhase1ToSentEmailAddress,
                            )
                          }
                          className="ms-n8 px-16"
                        >
                          {t('bookingDetails:bookingInfo.messaging.send')}
                        </Button>
                      </InputGroup>
                    </div>
                  )}
                  {showClientCancellationEmailBox(item) && (
                    <div className="row my-48 gx-48">
                      <div className="col-md-4">
                        <h3 className="h4 mb-16">
                          {t(
                            'bookingDetails:bookingInfo.messaging.cancellation.title',
                          )}
                        </h3>
                        <Form.Label
                          htmlFor="clientEmailCancellation"
                          className="required"
                        >
                          {t(
                            'bookingDetails:bookingInfo.messaging.phase1.emailLabel',
                          )}
                        </Form.Label>
                        <InputGroup className="flex-nowrap">
                          <InputText
                            Id="clientEmailCancellation"
                            Name="clientEmailCancellation"
                            IsRequired
                            HandleChange={event =>
                              setClientBookingCancellationToSentEmailAddress(
                                event.target.value,
                              )
                            }
                            Value={clientBookingCancellationToSentEmailAddress}
                            ClassName="mb-0 flex-fill rounded-end-0"
                          />
                          <Button
                            onClick={() =>
                              sendClientCancellationConfirmation(item.id)
                            }
                            disabled={
                              !isValidEmails(
                                clientBookingCancellationToSentEmailAddress,
                              )
                            }
                            className="ms-n8 px-16"
                          >
                            {t('bookingDetails:bookingInfo.messaging.send')}
                          </Button>
                        </InputGroup>
                      </div>
                    </div>
                  )}
                  {!item.crossSiteBooking && item.payerId != null && (
                    <div className="row my-48 gx-48">
                      <div className="col-md-4">
                        <h3 className="h4 mb-16">
                          {t(
                            'bookingDetails:bookingInfo.messaging.invoices.title',
                          )}
                        </h3>
                        <Form.Label
                          htmlFor="clientEmailBills"
                          className="required"
                        >
                          {t(
                            'bookingDetails:bookingInfo.messaging.invoices.emailLabel',
                          )}
                        </Form.Label>
                        <InputGroup className="flex-nowrap">
                          <InputText
                            Id="clientEmailBills"
                            Name="clientEmailBills"
                            IsRequired
                            HandleChange={event =>
                              setClientBookingBillsToSentEmailAddress(
                                event.target.value,
                              )
                            }
                            Value={clientBookingBillsToSentEmailAddress}
                            ClassName="mb-0 flex-fill rounded-end-0"
                          />
                          <Button
                            onClick={() => sendClientBillsFromBooking(item.id)}
                            disabled={
                              !isValidEmail(
                                clientBookingBillsToSentEmailAddress,
                              )
                            }
                            className="ms-n8 px-16"
                          >
                            {t('bookingDetails:bookingInfo.messaging.send')}
                          </Button>
                        </InputGroup>
                      </div>
                    </div>
                  )}
                  <div className="row my-48 gx-48">
                    <div className="col">
                      <BookingOwnersEmailComponent
                        bookingId={item.id}
                        bookingOwners={
                          getOwnersMaintainersForBooking(item).bookingOwners
                        }
                        sendOwnerBookingConfirmation={
                          sendOwnerBookingConfirmation
                        }
                      />
                    </div>
                    <div className="col">
                      <BookingMaintainersEmailComponent
                        bookingId={item.id}
                        bookingMaintainersAsRoles={
                          getOwnersMaintainersForBooking(item)
                            .bookingMaintainersAsRoles
                        }
                        bookingMaintainers={
                          getOwnersMaintainersForBooking(item)
                            .bookingMaintainers
                        }
                        sendMaintainerBookingConfirmation={
                          sendMaintainerBookingConfirmation
                        }
                      />
                    </div>
                  </div>
                  <hr className="mt-32" />
                  <div className="row mt-32 mb-24 gx-48">
                    <div className="col">
                      <Button onClick={() => refreshBookingMessageEvents()}>
                        {t(
                          'bookingDetails:bookingInfo.messaging.messageEvents.refreshButton',
                        )}
                      </Button>
                    </div>
                    <div className="col">
                      <Loader
                        IsLoading={
                          BookingMessageEventsStatus === LOADING_STATUS.LOADING
                        }
                      />
                    </div>
                  </div>
                  {bookingMessageEventsContainer != null && (
                    <Table bordered className="table-data mt-16">
                      <thead>
                        <tr key="bookingMessageEventsHeader2">
                          <th className="ps-24">
                            {t(
                              'bookingDetails:bookingInfo.messaging.events.table.title',
                            )}
                          </th>
                          <th className="ps-24">
                            {t(
                              'bookingDetails:bookingInfo.messaging.events.table.messageType',
                            )}
                          </th>
                          <th className="ps-24">
                            {t(
                              'bookingDetails:bookingInfo.messaging.events.table.emailAddress',
                            )}
                          </th>
                          <th className="ps-24">
                            {t(
                              'bookingDetails:bookingInfo.messaging.events.table.phoneNumber',
                            )}
                          </th>
                          <th className="ps-24">
                            {t(
                              'bookingDetails:bookingInfo.messaging.events.table.actions',
                            )}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookingMessageEventsContainer
                          .filter(
                            be =>
                              be.bookingId.toLowerCase() ===
                              item.id.toLowerCase(),
                          )
                          .slice()
                          .sort(
                            (a, b) =>
                              new Date(b.sentTime).getTime() -
                              new Date(a.sentTime).getTime(),
                          ) // Sorting by sentTime
                          .map((messageEvent, orderIndex) => (
                            <tr
                              key={generateItemKey(orderIndex, messageEvent.id)}
                            >
                              <td>
                                {new Date(
                                  Date.parse(messageEvent.sentTime),
                                ).toLocaleString('fi-FI', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}{' '}
                              </td>
                              <td>
                                {messageEventTypeTranslations.fi[
                                  messageEvent.bookingMessageEventType
                                ] || t('common:texts.unknown')}
                              </td>
                              <td>{messageEvent.emailAddresses}</td>
                              <td>{messageEvent.phoneNumbers}</td>
                              <td>
                                {messageEvent.transactionalMessageDtoJson?.trim() && (
                                  <button
                                    type="button"
                                    className="text-blue-600 underline hover:text-blue-800 border-0 bg-transparent p-0"
                                    onClick={e => {
                                      e.preventDefault();
                                      const popup = window.open(
                                        '',
                                        '_blank',
                                        `width=1000,height=700,scrollbars=yes,resizable=yes,left=${window.screen.width / 2 - 500},top=${window.screen.height / 2 - 350}`,
                                      );
                                      if (
                                        popup &&
                                        messageEvent.transactionalMessageDtoJson
                                      ) {
                                        const prettyJson = JSON.stringify(
                                          JSON.parse(
                                            messageEvent.transactionalMessageDtoJson,
                                          ),
                                          null,
                                          2,
                                        );
                                        popup.document.write(`
                                              <html>
                                                <head>
                                                  <title>${t('bookingDetails:bookingInfo.messaging.events.jsonPopup.titleJson')}</title>
                                                  <style>
                                                    body { font-family: monospace; padding: 1rem; background: #f9f9f9; color: #333; }
                                                    pre { white-space: pre-wrap; word-wrap: break-word; }
                                                  </style>
                                                </head>
                                                <body>
                                                  <h2>${t('bookingDetails:bookingInfo.messaging.events.jsonPopup.title')}</h2>
                                                  <pre>${prettyJson}</pre>
                                                </body>
                                              </html>
                                            `);
                                        popup.document.close();
                                      }
                                    }}
                                  >
                                    {t(
                                      'bookingDetails:bookingInfo.messaging.events.table.viewJson',
                                    )}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}
            </React.Fragment>
          ));
  const getTitle = (translations: ReturnType<typeof useTranslation>['t']) => {
    if (
      bookingContainer !== null &&
      bookingContainer !== undefined &&
      bookingContainer.id !== '00000000-0000-0000-0000-000000000000' &&
      bookingContainer.bookingResponses.length > 1
    ) {
      return translations('bookingDetails:header.titleGroupBooking');
    }
    if (
      bookingContainer !== null &&
      bookingContainer !== undefined &&
      bookingContainer.id !== '00000000-0000-0000-0000-000000000000' &&
      bookingContainer.bookingContainerStatus === 1
    ) {
      return translations('bookingDetails:header.titleConfirmedBooking');
    }
    if (
      bookingContainer !== null &&
      bookingContainer !== undefined &&
      bookingContainer.id !== '00000000-0000-0000-0000-000000000000' &&
      bookingContainer.bookingContainerStatus === 2
    ) {
      return translations('bookingDetails:header.titleFinalizedBooking');
    }
    if (
      bookingContainer !== null &&
      bookingContainer !== undefined &&
      bookingContainer.id !== '00000000-0000-0000-0000-000000000000' &&
      bookingContainer.bookingContainerStatus === 3
    ) {
      return translations('bookingDetails:header.titleCancelledBooking');
    }
    return translations('bookingDetails:header.titleDraftBooking');
  };

  return (
    <>
      <Helmet>
        <title>{[t('bookingDetails:title')]}</title>
      </Helmet>
      {bookingContainer !== null &&
        bookingContainer !== undefined &&
        bookingContainer.id !== '00000000-0000-0000-0000-000000000000' && (
          <Container className="container-content">
            <SectionHeader
              BackLink={backLinkUrl}
              Breadcrumbs={[t('bookingDetails:header.breadcrumb')]}
              Title={getTitle(t)}
            />
            <Loader
              IsLoading={bookingDetailsLoadingStatus === LOADING_STATUS.LOADING}
            />
            <Loader IsLoading={showLoader} />
            <div className="mb-32">
              <div className="mb-0">{bookingItems}</div>
            </div>
            {bookingContainer.bookingResponses.length > 1 && (
              <tr>
                <td style={{ textAlign: 'right' }} colSpan={11}>
                  {t(
                    'bookingDetails:bookingInfo.totals.basketresellerTotalAmount',
                  )}
                </td>
                <td>
                  {bookingContainer
                    ? bookingContainer.containerTravelTotal
                    : ''}
                </td>
                <td style={{ textAlign: 'right' }} colSpan={13}>
                  {t(
                    'bookingDetails:bookingInfo.totals.basketbookingTotalAmount',
                  )}
                </td>
                <td>
                  {bookingContainer ? bookingContainer.containerTotal : ''}
                </td>
              </tr>
            )}
            {!isTravelAgencyUser &&
              bookingContainer.bookingResponses.filter(
                bi =>
                  !bi.isCancelled && bi.isAllowedToCancel && bi.isAllowedToEdit,
              ).length > 0 && (
                <ActionButtons
                  CancelButtonText={t('bookingDetails:goBack')}
                  DeleteButtonText={
                    bookingContainer.bookingContainerStatus === 3
                      ? ''
                      : t('bookingDetails:cancelBasket')
                  }
                  HandleDelete={
                    bookingContainer.bookingContainerStatus === 3
                      ? null
                      : () => showCancelContainerModal(bookingContainer)
                  }
                  HandleSave={null}
                />
              )}

            <NewBookingModal
              editedBooking={editedBooking}
              webshopCustomer={
                editedBooking !== null && editedBooking !== undefined
                  ? editedBooking.webShopCustomer
                  : null
              }
              bookingTypeParam={bookingTypeParam}
              setShowNewBookingModal={setShowEditBookingModal}
              showNewBookingModal={showEditBookingModal}
              setEditedBooking={setEditedBooking}
              setBookingContainer={setBookingContainer}
              setBasketContainer={setBasketContainer}
            />
            <AdditionalBookingModal
              editedBooking={editedBooking}
              webshopCustomer={
                editedBooking !== null && editedBooking !== undefined
                  ? editedBooking.webShopCustomer
                  : null
              }
              setShowAdditionalBookingModal={setShowAdditionalBookingModal}
              showAdditionalBookingModal={showAdditionalBookingModal}
              setEditedBooking={setEditedBooking}
              setBookingContainer={setBookingContainer}
              setBasketContainer={setBasketContainer}
              refresh={false}
              pastBooking={getAllowPastAdditional(editedBooking)}
            />
            {/* {bookingContainer.bookingContainerStatus === 3 && ( */}
            {/*  <Alert variant="danger"> */}
            {/*    {t('bookingDetails:basketCancelled')} */}
            {/*  </Alert> */}
            {/* )} */}
          </Container>
        )}
      {(bookingContainer === null || bookingContainer === undefined) &&
        bookingDetailsLoadingStatus !== LOADING_STATUS.LOADING && (
          <Container className="container-content">
            <SectionHeader
              BackLink={backLinkUrl}
              Breadcrumbs={[t('bookingDetails:header.breadcrumb')]}
              Title={t('bookingDetails:header.titleBookingNotFound')}
            />
          </Container>
        )}
      {(bookingContainer === null || bookingContainer === undefined) &&
        bookingDetailsLoadingStatus === LOADING_STATUS.LOADING && (
          <Loader IsLoading />
        )}
      <DeleteConfirmationWithReason
        CancelButtonText={t('common:buttons.close')}
        ConfirmDelete={cancelBookingLocal}
        DeleteButtonText={t('bookingDetails:cancellation.cancelBooking')}
        HideModal={() => setShowDeleteModal(false)}
        Id={editedBooking ? editedBooking.id : ''}
        Message={t('bookingDetails:cancellation.cancelBookingConfirmation')}
        ShowModal={showDeleteModal}
        Title={t('bookingDetails:cancellation.cancelBookingTitle')}
      />
      <CustomerNoteModal
        show={editCustomerNote}
        onHide={() => setEditCustomerNote(false)}
        titleText={t('bookingDetails:bookingInfo.communication.notes.customer')}
        closeText={t('common:buttons.close')}
        saveText={t('common:buttons.save')}
        bookingContainer={bookingContainer}
        bookingId={customerNoteModalBookingId}
        noteId={customerNoteModalNoteId}
        onChange={handleBookingCustomerNoteChange}
        onSave={handleBookingCustomerNoteSave}
      />
      <OwnerNoteModal
        show={editOwnerNote}
        onHide={() => setEditOwnerNote(false)}
        titleText={t('bookingDetails:bookingInfo.communication.notes.owner')}
        closeText={t('common:buttons.close')}
        saveText={t('common:buttons.save')}
        bookingContainer={bookingContainer}
        bookingId={ownerNoteModalBookingId}
        noteId={ownerNoteModalNoteId}
        onChange={handleBookingOwnerNoteChange}
        onSave={handleBookingOwnerNoteSave}
      />
      <OfficerNoteModal
        show={editOfficerNote}
        onHide={() => setEditOfficerNote(false)}
        titleText={t('bookingDetails:bookingInfo.communication.notes.clerk')}
        closeText={t('common:buttons.close')}
        saveText={t('common:buttons.save')}
        bookingContainer={bookingContainer}
        bookingId={officerNoteModalBookingId}
        noteId={officerNoteModalNoteId}
        onChange={handleBookingOfficerNoteChange}
        onSave={handleBookingOfficerNoteSave}
      />
      <DeleteConfirmationWithReason
        CancelButtonText="Sulje"
        ConfirmDelete={cancelContainerLocal}
        DeleteButtonText={t('bookingDetails:cancellation.cancelBasket')}
        HideModal={() => setShowDeleteContainerModal(false)}
        Id={bookingContainer ? bookingContainer.id : ''}
        Message={t('bookingDetails:cancellation.cancelBasketConfirmation')}
        ShowModal={showDeleteContainerModal}
        Title={t('bookingDetails:cancellation.cancelBasketTitle')}
      />
      {!isTravelAgencyUser && (
        <div className="container-content container">
          <Button
            className="mb-16 w-100 btn btn-danger"
            onClick={() => setInBasketAndNavigate()}
            variant="secondary"
            size="lg"
            disabled={false}
          >
            {t('bookingDetails:buttons.goToBasketAndAddMore')}
          </Button>
        </div>
      )}
    </>
  );
}
export default BookingDetails;
